import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError, mergeCapabilityApplies, mergeCapabilityFor, mergeCapabilityResponseCurrent, Product, ProductSale, Category, ItemCreate, ItemUpdate, ItemDetail, ItemHistory, InventoryStats, Recipe, RecipeMaterial, RecipeMaterialSearch, SalesHistoryRow, type MergeCapabilityState, type MergePreview } from "../api";
import { formatVND, formatDate } from "../utils";
import { GlassPanel, Modal, toast, confirm, SkeletonTable, ErrorState, DataTable, KpiCard, Badge, FilePicker, SearchTextarea, type Column } from "../components";
import BarcodeScanner from "../components/BarcodeScanner";
import { useResyncListener } from "../hooks";
import { loadCatalog, setCachedCatalog, catalogSearchFields } from "../lib/productCatalog";
import { validateCategoryBulkResponse } from "../lib/categoryBulkResponse";
import { chunkTargets, mergeTargetResults } from "../lib/categoryBulkChunks";
import {
  classifyMergeFailure,
  classifyMergePayload,
  classifyMergeStatus,
  clearStoredMergeOperation,
  currentMergeLegacyStorageScope,
  currentMergeStorageScope,
  mergeAcksSatisfied,
  mergeExecutionBlockedReason,
  mergeExecutionUnavailableReason,
  mergePendingPairConflict,
  MERGE_OFFLINE_RISK,
  prepareMergeCommit,
  readMergeStorageSnapshot,
  type MergeAckState,
  type MergeOutcomeClass,
  type MergeStatus,
  type StoredMergeOperation,
} from "../lib/mergeCommit";
import { matchesSearchFields, rankSearchResults, searchItems } from "../search";

/** yyyy-mm-dd → ISO end of that local day 23:59:59.999 */
function dateOnlyEndToIso(v: string): string | null {
  if (!v || !v.trim()) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** datetime-local value → ISO (or null if empty). */
function localDateTimeToIso(v: string): string | null {
  if (!v || !v.trim()) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

/** Local today as yyyy-mm-dd */
function todayDateInput(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** End of current local week (Sunday) as yyyy-mm-dd */
function endOfWeekDateInput(): string {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const add = day === 0 ? 0 : 7 - day;
  d.setDate(d.getDate() + add);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function salePriceFromPercent(list: number, pct: number): number {
  if (!(list > 0) || !(pct >= 1 && pct <= 99)) return 0;
  return Math.round((list * (100 - pct)) / 100);
}

/** Multi-line search: 1 dòng = 1 token (tên/SKU/barcode). */
function searchTokens(raw: string): string[] {
  return raw.split(/\r?\n/).map((s) => s.trim()).filter((s) => s.length >= 2);
}

/** Filter the cached catalog locally with the same rank/merge helper the server path used. */
function filterCatalog(products: Product[], query: string): Product[] {
  const tokens = searchTokens(query);
  // searchTokens drops 1-char lines; a lone 1-char query still searched server-side before → keep parity.
  if (tokens.length === 0) {
    const single = query.trim();
    if (!single) return products;
    return searchItems(
      products,
      single,
      catalogSearchFields,
      { mode: "and" },
    ).map(({ item }) => item);
  }
  return rankSearchResults(
    products,
    tokens,
    catalogSearchFields,
    { mode: "and" },
  ).map(({ item }) => item);
}

/* ═══ Auto-gen SKU + Barcode ═══ */
const removeVietnameseAccents = (str: string): string => {
  return str.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/g, "d").replace(/Đ/g, "D");
};
const slugifySKU = (name: string, variantName?: string): string => {
  const base = removeVietnameseAccents(name).toUpperCase().replace(/[^A-Z0-9]+/g, "").slice(0, 6) || "SKU";
  const vSlug = variantName ? "-" + removeVietnameseAccents(variantName).toUpperCase().replace(/[^A-Z0-9]+/g, "").slice(0, 4) : "";
  const stamp = Date.now().toString().slice(-5);
  return `${base}${vSlug}-${stamp}`;
};
	const genBarcode = (): string => {
	  // Code128 in-store: "2" + 7 digits. Server also gens if client sends empty.
	  return "2" + Math.floor(Math.random() * 10_000_000).toString().padStart(7, "0");
	};

/* ═══ Types ═══ */
interface VariantForm {
  variant_id?: string;
  variant_name: string;
  sku: string;
  barcode: string;
  default_price: number;
  cost: number;
  initial_stock: number;
  supplier_name: string;
}

/** Danh sách unit cùng dimension với base_unit (cho recipe drawer selector). */
function unitsForBase(base?: string): string[] {
  if (!base) return ["pcs", "g", "kg", "ml", "l"];
  if (base === "g") return ["g", "kg"];
  if (base === "ml") return ["ml", "l"];
  return ["pcs", "cái", "chục", "hộp"];
}

interface ProductForm {
  item_name: string;
  description: string;
  category_id: string;
  image_url: string;
  track_stock: boolean;
  color: string;
  is_vat: boolean;
  is_bangke: boolean;
  is_composite: boolean;
  variants: VariantForm[];
}

const emptyVariant = (): VariantForm => ({
  variant_name: "", sku: "", barcode: "", default_price: 0, cost: 0, initial_stock: 0, supplier_name: "",
});

const emptyForm = (): ProductForm => ({
  item_name: "", description: "", category_id: "", image_url: "",
  track_stock: true, color: "", is_vat: false, is_bangke: false, is_composite: false,
  variants: [emptyVariant()],
});

/* ═══ Product Image Thumbnail ═══ */
function ProductImageThumb({ productId }: { productId: string }) {
  const [url, setUrl] = useState<string>("");
  const [hasImage, setHasImage] = useState<boolean | null>(null);

  useEffect(() => {
    let active = true;
    let blobUrl = "";
    api.productImages.list(productId).then((r) => {
      if (!active) return;
      if (r.images && r.images.length > 0) {
        setHasImage(true);
        api.productImages.loadBlob(r.images[0].id).then((u) => {
          if (active) { blobUrl = u; setUrl(u); }
        }).catch(() => {});
      } else {
        setHasImage(false);
      }
    }).catch(() => { if (active) setHasImage(false); });
    return () => { active = false; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [productId]);

  if (hasImage === null) return <span className="material-symbols-outlined">inventory_2</span>;
  if (hasImage === false) return <span className="material-symbols-outlined">inventory_2</span>;
  if (!url) return <div style={{ width: 48, height: 48, borderRadius: 6, background: "var(--bg-tertiary)", display: "flex", alignItems: "center", justifyContent: "center" }}><span className="material-symbols-outlined" style={{ fontSize: 16, opacity: 0.4 }}>image</span></div>;
  return <img src={url} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />;
}

function SaleCheckbox({ checked, label, onToggle, disabled = false }: { checked: boolean; label: string; onToggle: () => void; disabled?: boolean }) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: 20, height: 20, padding: 0, borderRadius: 4,
        border: `1px solid ${checked ? "var(--accent-blue)" : "var(--border-strong)"}`,
        background: checked ? "var(--accent-blue)" : hovered ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.04)",
        color: "white", display: "inline-flex", alignItems: "center", justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, outline: focused ? "2px solid var(--accent-blue)" : "none", outlineOffset: 2,
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      {checked && <span className="material-symbols-outlined" style={{ fontSize: 16, lineHeight: 1 }}>check</span>}
    </button>
  );
}

/* ═══ Component ═══ */
export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  // Unfiltered catalog for the active category — typing filters this locally (no D1 scan per keystroke).
  const [catalog, setCatalog] = useState<Product[]>([]);
  const catalogCategoryRef = useRef<string | null>(null);
  /** True once the unfiltered catalog fetch failed with nothing cached → legacy server search. */
  const catalogFailedRef = useRef(false);
  /** Optimistic row edits must touch the catalog too, or the next keystroke restores stale rows. */
  const mutateProducts = (update: (rows: Product[]) => Product[]) => {
    setProducts(update);
    setCatalog(update);
  };
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  /** Current debounced query for async callbacks (a fetch can resolve after `q` moved). */
  const qRef = useRef(q);
  qRef.current = q;
  /** Category whose unfiltered fetch is in flight — typing must not start a second one. */
  const pendingCategoryRef = useRef<string | null | undefined>(undefined);
  const qTimer = useRef<number | undefined>(undefined);
  const productsRequestGeneration = useRef(0);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [categoryId, setCategoryId] = useState<string>("");
  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);
  const advancedFilterActive = categoryId !== "";
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  // Trang card dùng CHUNG danh sách với select-all ⇒ "chọn trang này" luôn = đúng SP đang hiện.
  const cardPageProducts = useMemo(
    () => products.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [products, currentPage, pageSize],
  );
  const cardTotalPages = Math.max(1, Math.ceil(products.length / pageSize));
  // Đổi filter/tìm kiếm → về trang 1; danh sách co lại → kẹp trang để không hiện trang rỗng.
  useEffect(() => { setCurrentPage(1); }, [q, categoryId]);
  useEffect(() => { setCurrentPage((p) => Math.min(p, cardTotalPages)); }, [cardTotalPages]);

  // View mode + tabs (P3 enhancements)
  const [viewMode, setViewMode] = useState<"table" | "card">(
    () => (localStorage.getItem("products_view_mode") as "table" | "card") || "table",
  );
  const [activeTab, setActiveTab] = useState<"products" | "categories" | "sales">("products");
  // Đổi tab / chế độ xem → về trang 1 (card và table có state trang riêng, tránh lệch trang).
  useEffect(() => { setCurrentPage(1); }, [activeTab, viewMode]);
  const [editingCell, setEditingCell] = useState<
    { id: string; field: string; value: string; variantId: string } | null
  >(null);
  const productEditCancelledRef = useRef(false);
  const productEditSavingRef = useRef(false);
  const [stats, setStats] = useState<InventoryStats | null>(null);

  // ── Sale promo tab ──
  const [sales, setSales] = useState<ProductSale[]>([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState<string | null>(null);
  const [salesStatus, setSalesStatus] = useState<"active" | "all">("active");
  const [salesListQ, setSalesListQ] = useState("");
  const [editingSale, setEditingSale] = useState<
    { id: number; field: "sale_price" | "ends_at"; value: string } | null
  >(null);
  const saleEditCancelledRef = useRef(false);
  const saleEditSavingRef = useRef(false);
  const saleExpiryEditorRef = useRef<HTMLDivElement>(null);
  const [saleExpiryAnchorRect, setSaleExpiryAnchorRect] = useState<DOMRect | null>(null);
  const [saleExpiryAdvanced, setSaleExpiryAdvanced] = useState(false);
  const [salePrintSelected, setSalePrintSelected] = useState<Set<number>>(new Set());
  const [saleVisibleIds, setSaleVisibleIds] = useState<string[]>([]);
  const [showAddSale, setShowAddSale] = useState(false);
  const [saleProductQ, setSaleProductQ] = useState("");
  const [saleProductHits, setSaleProductHits] = useState<Product[]>([]);
  const [saleSelected, setSaleSelected] = useState<Product | null>(null);
  const [salePriceMode, setSalePriceMode] = useState<"fixed" | "percent">("fixed");
  const [salePrice, setSalePrice] = useState("");
  const [salePercent, setSalePercent] = useState("10");
  const [saleEndsDate, setSaleEndsDate] = useState(""); // yyyy-mm-dd or ""
  const [saleEndsAdvanced, setSaleEndsAdvanced] = useState(false);
  const [saleEndsAt, setSaleEndsAt] = useState(""); // datetime-local when advanced
  const [saleNote, setSaleNote] = useState("");
  const [saleSubmitting, setSaleSubmitting] = useState(false);
  const saleSearchTimer = useRef<number | undefined>(undefined);
  const salesListSearchTimer = useRef<number | undefined>(undefined);
  const [saleHistory, setSaleHistory] = useState<ItemHistory | null>(null);
  const [saleHistoryLoading, setSaleHistoryLoading] = useState(false);
  const [saleAllSales, setSaleAllSales] = useState<ProductSale[] | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editDetail, setEditDetail] = useState<ItemDetail | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkMd, setBulkMd] = useState("");
  const [importing, setImporting] = useState(false);
  const [showHistory, setShowHistory] = useState<string | null>(null);
  const [historyMeta, setHistoryMeta] = useState<{ id: string; name: string; sku?: string | null } | null>(null);
  const [historySales, setHistorySales] = useState<SalesHistoryRow[]>([]);
  const [historyLots, setHistoryLots] = useState<any[]>([]);
  const [historyAudit, setHistoryAudit] = useState<any[]>([]);
  const [historyOverview, setHistoryOverview] = useState<ItemHistory | null>(null);
  const [historyOverviewLoading, setHistoryOverviewLoading] = useState(false);
  const [historyTab, setHistoryTab] = useState<"overview" | "sales" | "lots" | "audit">("overview");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // ── Recipe drawer (W7) ──
  const [recipeProduct, setRecipeProduct] = useState<Product | null>(null);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [recipeMaterials, setRecipeMaterials] = useState<RecipeMaterial[]>([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipeSearchQ, setRecipeSearchQ] = useState("");
  const [recipeSearchResults, setRecipeSearchResults] = useState<RecipeMaterialSearch[]>([]);
  const [recipeSearchOpen, setRecipeSearchOpen] = useState(false);
  const [recipeSaving, setRecipeSaving] = useState(false);
  const [recipeError, setRecipeError] = useState<string | null>(null);
  const [cogsMap, setCogsMap] = useState<Map<string, number>>(new Map());
  const recipeAbandonedRef = useRef(false);
  const recipeSearchTimer = useRef<number | null>(null);
  const recipeLoadingForRef = useRef<string | null>(null);

  // U2: cleanup timer khi unmount tránh leak
  useEffect(() => {
    return () => {
      if (recipeSearchTimer.current) clearTimeout(recipeSearchTimer.current);
    };
  }, []);

  // Categories tab state
  const [showCatModal, setShowCatModal] = useState(false);
  const [catForm, setCatForm] = useState<{ id?: string; name: string; color: string; has_recipe: boolean }>({ name: "", color: "GREY", has_recipe: false });
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [tableVisibleIds, setTableVisibleIds] = useState<string[]>([]);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<"category" | "price" | "sale" | "vat" | "bangke" | "import_date" | null>(null);
  const [bulkValue, setBulkValue] = useState("");
  const [bulkEndsDate, setBulkEndsDate] = useState("");
  const [bulkMode, setBulkMode] = useState<"fixed" | "percent">("fixed");
  const [bulkEnabled, setBulkEnabled] = useState(true);
  const [bulkSaleAction, setBulkSaleAction] = useState<"create" | "set_expiry" | "end">("create");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkCategoryTargets, setBulkCategoryTargets] = useState<Array<{ item_id: string; item_name: string; sku: string; current_category_id: string | null }>>([]);
  const [bulkCategoryFailures, setBulkCategoryFailures] = useState<Array<{ item_id: string; error: string; reconciliation_required?: boolean }>>([]);
  const [bulkCategoryProgress, setBulkCategoryProgress] = useState<{ completed: number; total: number } | null>(null);
  // ── Gộp sản phẩm: preview và xác nhận có kiểm soát ──
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergePair, setMergePair] = useState<[string, string] | null>(null);
  const [mergeCanonicalId, setMergeCanonicalId] = useState<string | null>(null);
  const [mergePreview, setMergePreview] = useState<MergePreview | null>(null);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [mergeAcks, setMergeAcks] = useState<MergeAckState>({ sales_stopped: false, devices_synced: false, promo: false });
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  const [mergeOutcome, setMergeOutcome] = useState<MergeOutcomeClass | null>(null);
  const [mergeStatus, setMergeStatus] = useState<MergeStatus | null>(null);
  const [mergeStatusLoading, setMergeStatusLoading] = useState(false);
  // Operation identity survives modal close AND reload so an unknown result is
  // never re-run under a new id. Cleared only after a definite terminal phase.
  const [mergeOperation, setMergeOperation] = useState<StoredMergeOperation | null>(null);
  const [mergeRestored, setMergeRestored] = useState(false);
  const mergeScopeRef = useRef(currentMergeStorageScope());
  // Exact pointer that a terminal outcome may clear. Set from the prepared body,
  // never guessed from current React state (another tab may have written since).
  const mergeClearTargetRef = useRef<{ scope: string; operationId: string } | null>(null);
  const mergeSeqRef = useRef(0);
  const mergeSubmittingRef = useRef(false);
  const mergeStatusLoadingRef = useRef(false);
  const [mergeReloadKey, setMergeReloadKey] = useState(0);
  // Merge capability comes only from GET /auth/me (server-authoritative), tagged with
  // the identity it was fetched for. Missing/false/older server = denied.
  const [mergeCapability, setMergeCapability] = useState<MergeCapabilityState | null>(null);
  // Latest /auth/me probe wins: an older response for the SAME account must not
  // overwrite a newer verdict (e.g. a stale `allowed:true` re-enabling a revoked grant).
  const mergeCapabilitySeqRef = useRef(0);
  const loadMergeCapability = useCallback(() => {
    const scope = currentMergeStorageScope();
    const seq = ++mergeCapabilitySeqRef.current;
    setMergeCapability(null);
    if (!scope) return;
    const accept = (state: MergeCapabilityState | null) => {
      if (mergeCapabilityResponseCurrent(seq, mergeCapabilitySeqRef.current, scope, currentMergeStorageScope())) {
        setMergeCapability(state);
      }
    };
    api.me()
      .then((me) => accept(mergeCapabilityFor(scope, me)))
      .catch(() => accept(null));
  }, []);
  useEffect(() => {
    loadMergeCapability();
    // Unmount invalidates any in-flight probe so it cannot update state after teardown.
    return () => { mergeCapabilitySeqRef.current++; };
  }, [loadMergeCapability]);
  useEffect(() => {
    // Reload recovery is read-only: restore the pointer and let the owner press
    // "Kiểm tra trạng thái". Never auto-submit or auto-query.
    const scope = currentMergeStorageScope();
    mergeScopeRef.current = scope;
    const snapshot = readMergeStorageSnapshot(scope, undefined, currentMergeLegacyStorageScope());
    const stored = snapshot.operations[0] ?? null;
    if (stored) {
      setMergeOperation(stored);
      setMergeRestored(true);
      setMergeOutcome({
        kind: "pending",
        keepOperationId: true,
        message:
          snapshot.blockedReason || snapshot.operations.length > 1
            ? "Có nhiều thao tác gộp chưa xác định. Không gửi thao tác mới — kiểm tra trạng thái trước."
            : "Có thao tác gộp chưa xác định từ phiên trước. Kiểm tra trạng thái trước khi làm gì thêm.",
      });
    } else if (snapshot.blockedReason) {
      // Unreadable/incompatible data for this account: block new commits.
      setMergeRestored(true);
      setMergeOutcome({ kind: "pending", keepOperationId: true, message: snapshot.blockedReason });
    }
  }, []);
  useEffect(() => {
    // Account switch without reload: drop the previous identity's pointer and close
    // the modal. No automatic request is issued.
    const onFocus = () => {
      const scope = currentMergeStorageScope();
      // Revalidate the server capability on return to the tab (session may have
      // expired or the account may have changed in another tab).
      loadMergeCapability();
      if (scope === mergeScopeRef.current) return;
      mergeScopeRef.current = scope;
      mergeSeqRef.current++;
      setMergeOperation(null);
      setMergeRestored(false);
      setMergeStatus(null);
      setMergeOutcome(null);
      setMergeOpen(false);
      setMergePair(null);
      setMergeCanonicalId(null);
      setMergePreview(null);
      setMergeAcks({ sales_stopped: false, devices_synced: false, promo: false });
      mergeClearTargetRef.current = null;
      const snapshot = readMergeStorageSnapshot(scope, undefined, currentMergeLegacyStorageScope());
      const stored = snapshot.operations[0] ?? null;
      if (stored) {
        setMergeOperation(stored);
        setMergeRestored(true);
        setMergeOutcome({
          kind: "pending",
          keepOperationId: true,
          message: "Tài khoản đã đổi. Có thao tác gộp chưa xác định — kiểm tra trạng thái trước.",
        });
      } else if (snapshot.blockedReason) {
        setMergeRestored(true);
        setMergeOutcome({ kind: "pending", keepOperationId: true, message: snapshot.blockedReason });
      }
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadMergeCapability]);
  const mergeAllowed = mergeCapabilityApplies(mergeCapability, currentMergeStorageScope());
  const mergeCapabilityDenied =
    !!mergeCapability &&
    mergeCapability.scope === currentMergeStorageScope() &&
    mergeCapability.allowed !== true;
  const bulkSubmittingRef = useRef(false);
  const bulkCategoryValueRef = useRef("");
  const bulkCategoryRequestTokenRef = useRef(0);
  const categoryMutationSequenceRef = useRef(0);
  const categoryMutationTokensRef = useRef(new Map<string, number>());

  type CatDeleteItem = { id: string; item_name: string; sku: string | null };
  type CatDeleteState = {
    category: Category;
    items: CatDeleteItem[];
    total: number;
    targetId: string;
    failures: Array<{ item_id: string; error: string; reconciliation_required?: boolean }>;
    progress: { completed: number; total: number } | null;
  };
  const [catDelete, setCatDelete] = useState<CatDeleteState | null>(null);
  const [catDeleteBusy, setCatDeleteBusy] = useState(false);
  const catDeleteBusyRef = useRef(false);
  const catDeleteTokenRef = useRef(0);
  const PROTECTED_CAT_IDS = new Set(["cat_material", "cat_workshop"]);
  const isProtectedCat = (c: Category) => PROTECTED_CAT_IDS.has(c.id);

  // Dedup categories theo name (chống duplicate "Workshop" từ DB)
  const dedupCategories = (list: Category[]) =>
    list.filter((c, i, arr) => arr.findIndex((x) => x.name === c.name) === i);

  useEffect(() => {
    api.categories.list()
      .then((r) => setCategories(dedupCategories(r.categories)))
      .catch(() => {});
    api.inventoryStats().then(setStats).catch(() => {});
  }, []);

  // Auto mở Recipe drawer nếu vào bằng ?recipe=<product_id> (vd: từ Materials "SP sử dụng")
  const recipeAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (recipeAutoOpenedRef.current || products.length === 0) return;
    const pid = new URLSearchParams(window.location.search).get("recipe");
    if (!pid) return;
    const p = products.find((x) => x.id === pid);
    if (p) {
      recipeAutoOpenedRef.current = true;
      openRecipe(p);
    }
  }, [products]);

  useEffect(() => {
    localStorage.setItem("products_view_mode", viewMode);
  }, [viewMode]);

  const reloadCategories = () => api.categories.list().then((r) => setCategories(dedupCategories(r.categories))).catch(() => {});

  const loadProducts = (silent = false, canApply = () => true) => {
    const generation = ++productsRequestGeneration.current;
    const isCurrent = () => generation === productsRequestGeneration.current && canApply();
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    // One unfiltered fetch per category; typing filters this snapshot locally (no q → no D1 scan).
    const params = { category_id: categoryId || undefined, limit: 2000, include_total: 0 as const };
    pendingCategoryRef.current = categoryId;
    return api.products(params)
      .then((result) => {
        const list = result.products || [];
        if (!isCurrent()) return;
        catalogCategoryRef.current = categoryId;
        catalogFailedRef.current = false;
        setCatalog(list);
        // Share the global, unfiltered snapshot with ImportOrderPage search.
        if (!categoryId) setCachedCatalog(list);
        setProducts(filterCatalog(list, qRef.current));
        // U1: batch fetch COGS cho SP sản xuất (fire-and-forget, không block render)
        // Race-safe: GIỮ giá trị cũ trong lúc refetch (k xóa trước) → tránh "..." blink.
        // Chỉ dọn entry của item k còn trong list mới (bị filter out).
        const wsItems = list.filter((p) => hasRecipe(p));
        const wsIds = new Set(wsItems.map((p) => p.id));
        setCogsMap((prev) => {
          const next = new Map(prev);
          for (const id of next.keys()) if (!wsIds.has(id)) next.delete(id);
          return next;
        });
        if (wsItems.length) {
          Promise.all(
            wsItems.map((p) =>
              api.recipes.get(p.id)
                .then((rec) => [p.id, rec.total_cogs ?? 0] as const)
                .catch(() => null),
            ),
          ).then((rows) => {
            setCogsMap((prev) => {
              const next = new Map(prev);
              for (const row of rows) if (row) next.set(row[0], row[1]);
              return next;
            });
          });
        }
      })
      .catch((e) => {
        // No snapshot for this category → typing falls back to the legacy per-token server search.
        if (catalogCategoryRef.current !== categoryId) catalogFailedRef.current = true;
        if (!silent && isCurrent()) setError(e.message);
      })
      .finally(() => {
        if (pendingCategoryRef.current === categoryId) pendingCategoryRef.current = undefined;
        if (!silent && isCurrent()) setLoading(false);
      });
  };

  /** Legacy q-parameterized search — only used when the local catalog is unavailable. */
  const loadProductsFromServer = (silent = true) => {
    const generation = ++productsRequestGeneration.current;
    const isCurrent = () => generation === productsRequestGeneration.current;
    if (!silent) setLoading(true);
    const tokens = searchTokens(q);
    const params = { category_id: categoryId || undefined, limit: 2000, include_total: 0 as const };
    const request = tokens.length > 1
      ? Promise.all(tokens.map((token) => api.products({ ...params, q: token }))).then((results) => {
          const unique = new Map<string, Product>();
          for (const result of results) {
            for (const product of result.products || []) unique.set(product.id, product);
          }
          return filterCatalog([...unique.values()], q);
        })
      : api.products({ ...params, q: q.trim() || undefined }).then((result) => result.products || []);
    return request
      .then((list) => { if (isCurrent()) setProducts(list); })
      .catch((e) => { if (!silent && isCurrent()) setError(e.message); })
      .finally(() => { if (!silent && isCurrent()) setLoading(false); });
  };

  // Debounce search input → query
  useEffect(() => {
    if (qTimer.current) clearTimeout(qTimer.current);
    qTimer.current = window.setTimeout(() => setQ(qInput), 250);
    return () => { if (qTimer.current) clearTimeout(qTimer.current); };
  }, [qInput]);

  useEffect(() => {
    // Typing only re-filters the in-memory catalog; a category change (or cold start) refetches.
    if (catalogCategoryRef.current === categoryId) {
      setProducts(filterCatalog(catalog, q));
    } else if (catalogFailedRef.current) {
      loadProductsFromServer();
    } else if (pendingCategoryRef.current === categoryId) {
      // Already fetching this category — the response re-applies the latest query itself.
    } else {
      loadProducts(products.length > 0);
    }
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, categoryId]);

  // Listen for global sync (silent)
  useResyncListener(() => {
    loadProducts(true);
    if (activeTab === "sales") loadSales(true);
  });

  const loadSales = (silent = false) => {
    if (!silent) {
      setSalesLoading(true);
      setSalesError(null);
    }
    return api.productSales
      .list(salesStatus, salesListQ)
      .then((r) => setSales(Array.isArray(r.sales) ? r.sales : []))
      .catch((e) => {
        if (!silent) setSalesError(e.message || "Lỗi tải sale");
      })
      .finally(() => {
        if (!silent) setSalesLoading(false);
      });
  };

  useEffect(() => {
    const reloadOnFocus = () => {
      if (activeTab === "sales") loadSales(true);
      else loadProducts(true);
    };
    window.addEventListener("focus", reloadOnFocus);
    return () => window.removeEventListener("focus", reloadOnFocus);
  }, [activeTab, salesStatus, salesListQ, q, categoryId]);

  useEffect(() => {
    if (activeTab !== "sales") return;
    if (salesListSearchTimer.current) clearTimeout(salesListSearchTimer.current);
    salesListSearchTimer.current = window.setTimeout(() => loadSales(true), 250);
    return () => {
      if (salesListSearchTimer.current) clearTimeout(salesListSearchTimer.current);
    };
  }, [salesListQ]);

  useEffect(() => {
    if (activeTab === "sales") {
      loadSales(false);
      if (products.length === 0) loadProducts(true);
    }
  }, [activeTab, salesStatus]);

  const catMap = useMemo(() => {
    const m: Record<string, { name: string; color: string | null; has_recipe: number }> = {};
    for (const c of categories) m[c.id] = { name: c.name, color: c.color, has_recipe: c.has_recipe ?? 0 };
    return m;
  }, [categories]);

  const productById = useMemo(() => {
    const m = new Map<string, Product>();
    for (const p of products) m.set(p.id, p);
    return m;
  }, [products]);

  type SaleRow = ProductSale & { category_name: string };

  const saleRows = useMemo<SaleRow[]>(() => {
    const rows = sales.map((r) => {
      const p = productById.get(r.item_id);
      const category_name = (p && catMap[p.category_id ?? ""]?.name) || "—";
      return { ...r, category_name };
    });
    if (!salesListQ.trim()) return rows;
    return rows.filter((r) =>
      matchesSearchFields(
        { id: String(r.item_id), name: r.item_name, sku: r.sku, barcode: r.barcode, text: [r.category_name] },
        salesListQ,
        { mode: "and" },
      ),
    );
  }, [sales, salesListQ, productById, catMap]);

  const navigate = useNavigate();

  // In tem sale: các SP ĐANG sale (saleRows đã lọc theo ô tìm kiếm) → trang in tem
  // kèm giá gốc + giá sale để in tem khuyến mại. Pattern giống ImportOrderPage handlePrintLabels.
  const handlePrintSaleLabels = () => {
    const selectedRows = saleRows.filter((r) => salePrintSelected.has(r.id));
    const list = (selectedRows.length > 0 ? selectedRows : saleRows.filter((r) => r.status === "active"))
      .map((r) => ({
        id: r.item_id,
        qty: 1,
        stock: Math.max(0, Math.floor(productById.get(r.item_id)?.in_stock ?? 0)),
        list_price: r.list_price,
        sale_price: r.sale_price,
        name: r.item_name || "",
        sku: r.sku || "",
        barcode: r.barcode || "",
      }));
    if (list.length === 0) {
      toast.error("Không có SP đang sale để in tem");
      return;
    }
    sessionStorage.setItem("barcode_preselect", JSON.stringify(list));
    navigate("/ops/barcode");
  };

  // "Thêm in tem": đẩy SP đã chọn sang trang in tem qua sessionStorage (cùng contract với ImportOrderPage/ConsignmentPage).
  // Chỉ truyền {id, qty}; trang in tem tự load SKU/barcode/giá THẬT từ POS.
  const handleAddToPrint = useCallback((items: Product[]) => {
    const list = items.filter((p) => p && p.id).map((p) => ({ id: p.id, qty: 1 }));
    if (list.length === 0) {
      toast.error("Chưa chọn SP để in tem");
      return;
    }
    sessionStorage.setItem("barcode_preselect", JSON.stringify(list));
    navigate("/ops/barcode");
  }, [navigate]);

  // Debounced product search for "Thêm sale": name / SKU / barcode / category, multi-token AND
  useEffect(() => {
    if (!showAddSale || saleSelected) return;
    if (saleSearchTimer.current) clearTimeout(saleSearchTimer.current);
    const q = saleProductQ.trim();
    if (q.length < 1) {
      setSaleProductHits([]);
      return;
    }
    saleSearchTimer.current = window.setTimeout(() => {
      const fields = (p: Product) => ({
        id: p.id,
        name: p.item_name,
        sku: p.sku,
        barcode: p.barcode,
        text: [(p.category_id ? catMap[p.category_id]?.name : "") || p.category_name],
      });
      const pick = (pool: Product[]) => setSaleProductHits(
        searchItems(pool.filter((p) => !!p.variant_id), q, fields, { mode: "and" })
          .slice(0, 20)
          .map(({ item }) => item),
      );
      // Global catalog covers every category locally → no per-keystroke D1 scan.
      loadCatalog()
        .then((list) => {
          if (list.length > 0) { pick(list); return; }
          throw new Error("catalog unavailable");
        })
        .catch(() =>
          api
            .products({ q, limit: 30 })
            .then((r) => {
              const local = products.filter((p) => !!p.variant_id);
              const seen = new Set(local.map((p) => p.id));
              pick([...local, ...(r.products || []).filter((p) => !seen.has(p.id))]);
            })
            .catch(() => pick(products)),
        );
    }, 250);
    return () => {
      if (saleSearchTimer.current) clearTimeout(saleSearchTimer.current);
    };
  }, [saleProductQ, products, showAddSale, saleSelected, catMap]);

  // History for the picked product (info panel; failure never blocks sale create)
  useEffect(() => {
    if (!saleSelected) {
      setSaleHistory(null);
      return;
    }
    let alive = true;
    setSaleHistory(null);
    setSaleHistoryLoading(true);
    api.items
      .history(saleSelected.id)
      .then((h) => {
        if (alive) setSaleHistory(h);
      })
      .catch(() => {
        if (alive) setSaleHistory(null);
      })
      .finally(() => {
        if (alive) setSaleHistoryLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [saleSelected]);

  // Past sales of picked product need the "all" list when the tab shows "active" only
  useEffect(() => {
    if (!saleSelected || salesStatus === "all" || saleAllSales !== null) return;
    api.productSales
      .list("all")
      .then((r) => setSaleAllSales(Array.isArray(r.sales) ? r.sales : []))
      .catch(() => {});
  }, [saleSelected, salesStatus, saleAllSales]);

  const saleHistoryForProduct = useMemo(() => {
    if (!saleSelected?.variant_id) return [];
    const src = salesStatus === "all" ? sales : saleAllSales;
    if (!src) return [];
    return src.filter((s) => s.variant_id === saleSelected.variant_id);
  }, [saleSelected, sales, saleAllSales, salesStatus]);

  const saleRelated = useMemo(() => {
    const anchor = saleSelected || (saleProductQ.trim() ? saleProductHits[0] : null);
    if (!anchor?.category_id) return [];
    const pool = products.filter(
      (p) => !!p.variant_id && p.category_id === anchor.category_id && p.id !== anchor.id,
    );
    const query = saleProductQ.trim() || anchor.item_name;
    const ranked = searchItems(
      pool,
      query,
      (p) => ({ id: p.id, name: p.item_name, sku: p.sku, barcode: p.barcode }),
      { mode: "or" },
    );
    const matchedIds = new Set(ranked.map(({ item }) => item.id));
    return [
      ...ranked.map(({ item }) => item),
      ...pool.filter((p) => !matchedIds.has(p.id)),
    ].slice(0, 40);
  }, [saleSelected, saleProductHits, products, saleProductQ]);

  const openAddSale = () => {
    setSaleProductQ("");
    setSaleProductHits([]);
    setSaleSelected(null);
    setSaleHistory(null);
    setSaleAllSales(null);
    setSalePriceMode("fixed");
    setSalePrice("");
    setSalePercent("10");
    setSaleEndsDate("");
    setSaleEndsAdvanced(false);
    setSaleEndsAt("");
    setSaleNote("");
    setShowAddSale(true);
    if (products.length === 0) loadProducts(true);
  };

  const pickSaleProduct = (p: Product) => {
    setSaleSelected(p);
    setSaleProductQ(p.item_name);
    setSaleProductHits([]);
    const list = Math.round(Number(p.default_price) || 0);
    if (list > 0) {
      const pct = Math.min(99, Math.max(1, Math.round(Number(salePercent) || 10)));
      const fromPct = salePriceFromPercent(list, pct);
      const suggest =
        salePriceMode === "percent"
          ? fromPct
          : Math.max(0, Math.floor((list * 0.9) / 1000) * 1000);
      setSalePrice(suggest > 0 && suggest < list ? String(suggest) : "");
      if (salePriceMode === "percent") setSalePercent(String(pct));
    } else {
      setSalePrice("");
    }
  };

  const saleListPrice = Math.round(Number(saleSelected?.default_price) || 0);
  const saleComputedFromPct =
    salePriceMode === "percent" && saleListPrice > 0
      ? salePriceFromPercent(saleListPrice, Math.round(Number(salePercent) || 0))
      : 0;

  const handleCreateSale = async () => {
    if (!saleSelected?.variant_id) {
      toast.error("Chọn sản phẩm (cần variant)");
      return;
    }
    const list = saleListPrice;
    let price: number;
    if (salePriceMode === "percent") {
      const pct = Math.round(Number(salePercent));
      if (!Number.isFinite(pct) || pct < 1 || pct > 99) {
        toast.error("Giảm % phải từ 1–99");
        return;
      }
      if (!(list > 0)) {
        toast.error("SP chưa có giá gốc — không tính được %");
        return;
      }
      price = salePriceFromPercent(list, pct);
    } else {
      price = Math.round(Number(salePrice));
      if (!Number.isFinite(price) || !Number.isInteger(price) || price < 0) {
        toast.error("Giá sale phải là số nguyên VND ≥ 0");
        return;
      }
    }
    if (list > 0 && price >= list) {
      toast.error(`Giá sale phải < giá gốc (${formatVND(list)})`);
      return;
    }
    if (!(price >= 0) || !Number.isInteger(price)) {
      toast.error("Giá sale không hợp lệ");
      return;
    }
    let ends_at: string | null = null;
    if (saleEndsAdvanced) {
      ends_at = localDateTimeToIso(saleEndsAt);
      if (saleEndsAt.trim() && !ends_at) {
        toast.error("Thời gian hết hạn không hợp lệ");
        return;
      }
    } else if (saleEndsDate.trim()) {
      ends_at = dateOnlyEndToIso(saleEndsDate);
      if (!ends_at) {
        toast.error("Ngày hết hạn không hợp lệ");
        return;
      }
    }
    setSaleSubmitting(true);
    try {
      await api.productSales.create({
        variant_id: saleSelected.variant_id,
        sale_price: price,
        ends_at,
        note: saleNote.trim() || undefined,
      });
      toast.success("Đã tạo sale");
      setShowAddSale(false);
      loadSales(true);
      loadProducts(true);
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 409 && e.payload?.code === "active_sale_exists") {
        const price = Number(e.payload?.existing_sale_price);
        toast.error(
          Number.isFinite(price) && price > 0
            ? `Sản phẩm này đang có chương trình sale (giá ${formatVND(price)}) — xoá sale cũ trước khi tạo mới`
            : "Sản phẩm này đang có chương trình sale — xoá sale cũ trước khi tạo mới",
        );
      } else {
        toast.error(e.message || "Lỗi tạo sale");
      }
    } finally {
      setSaleSubmitting(false);
    }
  };

  const handleEndSale = async (row: ProductSale) => {
    const ok = await confirm({
      title: "Kết thúc sale",
      message: `Khôi phục giá gốc cho "${row.item_name || row.sku || row.variant_id}"? Giá sẽ về ${formatVND(row.list_price)}.`,
      danger: true,
      confirmLabel: "Kết thúc",
    });
    if (!ok) return;
    try {
      await api.productSales.end(row.id);
      toast.success("Đã kết thúc sale");
      loadSales(true);
      loadProducts(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi kết thúc sale");
    }
  };

  useEffect(() => {
    if (editingSale?.field !== "ends_at") return;
    const close = (event: MouseEvent) => {
      if (!saleExpiryEditorRef.current?.contains(event.target as Node)) {
        saleEditCancelledRef.current = true;
        setEditingSale(null);
        setSaleExpiryAnchorRect(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [editingSale]);

  const saveSaleEdit = async (row: ProductSale, nextValue?: string, advanced = false) => {
    if (saleEditCancelledRef.current) {
      saleEditCancelledRef.current = false;
      return;
    }
    if (saleEditSavingRef.current || !editingSale || editingSale.id !== row.id) return;
    const { field } = editingSale;
    const value = nextValue ?? editingSale.value;
    let patch: { sale_price?: number; ends_at?: string | null };
    if (field === "sale_price") {
      const sale_price = Number(value);
      if (!Number.isFinite(sale_price) || !Number.isInteger(sale_price) || sale_price < 0) {
        toast.error("Giá sale phải là số nguyên VND ≥ 0");
        return;
      }
      if (sale_price >= row.list_price) {
        toast.error(`Giá sale phải < giá gốc (${formatVND(row.list_price)})`);
        return;
      }
      patch = { sale_price };
    } else {
      const ends_at = value.trim() ? (advanced ? localDateTimeToIso(value) : dateOnlyEndToIso(value)) : null;
      if (value.trim() && !ends_at) {
        toast.error(advanced ? "Thời gian hết hạn không hợp lệ" : "Ngày hết hạn không hợp lệ");
        return;
      }
      patch = { ends_at };
    }
    saleEditSavingRef.current = true;
    setEditingSale(null);
    try {
      const result = await api.productSales.update(row.id, patch);
      if (result.ended) toast.success("Sale đã kết thúc (hạn đặt ở quá khứ)");
      else toast.success("Đã cập nhật sale");
      if (result.warning) toast.warning(result.warning);
      loadSales(true);
      loadProducts(true);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Lỗi cập nhật sale");
    } finally {
      saleEditSavingRef.current = false;
    }
  };

  const saleColumns: Column<SaleRow>[] = useMemo(
    () => [
      {
        key: "select",
        width: 44,
        align: "center",
        header: (() => {
          const visible = saleRows.filter((x) => saleVisibleIds.includes(String(x.id)));
          return (
            <SaleCheckbox
              label="Chọn trang sale này"
              checked={visible.length > 0 && visible.every((r) => salePrintSelected.has(r.id))}
              onToggle={() => setSalePrintSelected((prev) => {
                const next = new Set(prev);
                const allSelected = visible.length > 0 && visible.every((r) => next.has(r.id));
                for (const r of visible) allSelected ? next.delete(r.id) : next.add(r.id);
                return next;
              })}
            />
          );
        })(),
        render: (r) => (
          <SaleCheckbox
            label={`Chọn ${r.item_name || r.sku || r.item_id}`}
            checked={salePrintSelected.has(r.id)}
            onToggle={() => setSalePrintSelected((prev) => {
              const next = new Set(prev);
              next.has(r.id) ? next.delete(r.id) : next.add(r.id);
              return next;
            })}
          />
        ),
      },
      {
        key: "item_name",
        header: "Sản phẩm",
        sortable: true,
        render: (r) => <span style={{ fontWeight: 500 }}>{r.item_name || r.item_id || "—"}</span>,
      },
      {
        key: "category_name",
        header: "Danh mục",
        sortable: true,
        render: (r) => <span style={{ fontSize: 13 }}>{r.category_name}</span>,
      },
      {
        key: "sku",
        header: "SKU",
        sortable: true,
        render: (r) => (
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.sku || "—"}</span>
        ),
      },
      {
        key: "list_price",
        header: "Giá gốc",
        align: "right",
        sortable: true,
        render: (r) => formatVND(r.list_price),
      },
      {
        key: "sale_price",
        header: "Giá sale",
        align: "right",
        sortable: true,
        render: (r) => {
          const editing = editingSale?.id === r.id && editingSale.field === "sale_price";
          if (editing) return (
            <input
              autoFocus
              aria-label={`Giá sale ${r.item_name || r.item_id}`}
              inputMode="numeric"
              value={editingSale.value}
              onChange={(e) => setEditingSale({ ...editingSale, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") void saveSaleEdit(r);
                if (e.key === "Escape") {
                  saleEditCancelledRef.current = true;
                  setEditingSale(null);
                }
              }}
              onBlur={() => void saveSaleEdit(r)}
              style={{ width: 100, textAlign: "right" }}
            />
          );
          return (
            <button
              type="button"
              disabled={r.status !== "active"}
              onClick={() => {
                saleEditCancelledRef.current = false;
                setEditingSale({ id: r.id, field: "sale_price", value: String(r.sale_price) });
              }}
              title={r.status === "active" ? "Sửa giá sale" : undefined}
              style={{ fontWeight: 600, color: "var(--accent-red, #f87171)", cursor: r.status === "active" ? "pointer" : "default", padding: 0 }}
            >
              {formatVND(r.sale_price)}
            </button>
          );
        },
      },
      {
        key: "ends_at",
        header: "Hết hạn",
        sortable: true,
        render: (r) => {
          const editing = editingSale?.id === r.id && editingSale.field === "ends_at";
          if (editing && saleExpiryAnchorRect) {
            const width = 300;
            const viewportPadding = 8;
            const left = Math.max(
              viewportPadding,
              Math.min(
                saleExpiryAnchorRect.right > window.innerWidth - width - viewportPadding
                  ? saleExpiryAnchorRect.right - width
                  : saleExpiryAnchorRect.left,
                window.innerWidth - width - viewportPadding,
              ),
            );
            const top = Math.max(viewportPadding, Math.min(saleExpiryAnchorRect.bottom + 6, window.innerHeight - 220));
            return createPortal(
              <div
                ref={saleExpiryEditorRef}
                role="dialog"
                aria-label={`Hết hạn sale ${r.item_name || r.item_id}`}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    saleEditCancelledRef.current = true;
                    setEditingSale(null);
                    setSaleExpiryAnchorRect(null);
                  }
                }}
                style={{ position: "fixed", top, left, zIndex: 20, width, padding: 8, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 4px 12px rgba(0,0,0,.2)" }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 4, marginBottom: 8 }}>
                  <button type="button" className="btn btn-ghost" style={{ minWidth: 0, width: "100%", paddingInline: 0, padding: "4px 2px", whiteSpace: "nowrap", overflow: "hidden", textAlign: "center", fontSize: 12, color: "var(--text-primary)" }} onClick={() => void saveSaleEdit(r, todayDateInput())}>Hôm nay</button>
                  <button type="button" className="btn btn-ghost" style={{ minWidth: 0, width: "100%", paddingInline: 0, padding: "4px 2px", whiteSpace: "nowrap", overflow: "hidden", textAlign: "center", fontSize: 12, color: "var(--text-primary)" }} onClick={() => void saveSaleEdit(r, endOfWeekDateInput())}>Hết tuần</button>
                  <button type="button" className="btn btn-ghost" style={{ minWidth: 0, width: "100%", paddingInline: 0, padding: "4px 2px", whiteSpace: "nowrap", overflow: "hidden", textAlign: "center", fontSize: 12, color: "var(--text-primary)" }} onClick={() => void saveSaleEdit(r, "")}>Không hạn</button>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <input
                    autoFocus
                    aria-label="Ngày hết hạn"
                    type={saleExpiryAdvanced ? "datetime-local" : "date"}
                    value={editingSale.value}
                    onChange={(e) => setEditingSale({ ...editingSale, value: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") void saveSaleEdit(r, undefined, saleExpiryAdvanced); }}
                    style={{ minWidth: 0, background: "var(--bg-tertiary, transparent)", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: 8, colorScheme: "dark" }}
                  />
                  <button type="button" className="btn btn-secondary" onClick={() => void saveSaleEdit(r, undefined, saleExpiryAdvanced)}>Áp dụng</button>
                </div>
                <button type="button" className="btn btn-ghost" style={{ padding: "4px 0", marginTop: 4, fontSize: 12 }} onClick={() => {
                  const localValue = r.ends_at ? new Date(r.ends_at).toISOString().slice(0, 16) : "";
                  setSaleExpiryAdvanced((prev) => !prev);
                  setEditingSale({ ...editingSale, value: saleExpiryAdvanced ? r.ends_at?.slice(0, 10) || "" : localValue });
                }}>
                  Giờ cụ thể…
                </button>
              </div>,
              document.body,
            );
          }
          return (
            <button
              type="button"
              disabled={r.status !== "active"}
              onClick={(event) => {
                saleEditCancelledRef.current = false;
                setSaleExpiryAdvanced(false);
                setSaleExpiryAnchorRect(event.currentTarget.getBoundingClientRect());
                setEditingSale({ id: r.id, field: "ends_at", value: r.ends_at?.slice(0, 10) || "" });
              }}
              title={r.status === "active" ? "Sửa hạn sale" : undefined}
              style={{ opacity: r.ends_at ? 1 : 0.5, fontSize: 12, cursor: r.status === "active" ? "pointer" : "default", padding: 0 }}
            >
              {r.ends_at ? formatDate(r.ends_at) : "Không hạn"}
            </button>
          );
        },
      },
      {
        key: "status",
        header: "TT",
        render: (r) => (
          <Badge variant={r.status === "active" ? "ok" : "ghost"}>
            {r.status === "active" ? "Đang sale" : r.status === "ended" ? "Đã kết thúc" : r.status}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "",
        align: "right",
        render: (r) =>
          r.status === "active" ? (
            <button
              className="btn btn-ghost"
              style={{ padding: "4px 8px", color: "var(--accent-red)", fontSize: 12 }}
              onClick={() => handleEndSale(r)}
              title="Kết thúc sale · khôi phục giá gốc"
            >
              Kết thúc
            </button>
          ) : null,
      },
    ],
    [saleRows, salePrintSelected, saleVisibleIds, editingSale],
  );

  const hasRecipe = (p: Product) =>
    p.is_composite === 1 || ((catMap[p.category_id ?? ""]?.has_recipe ?? 0) === 1);

  const colorOf = (raw: string | null | undefined) => {
    if (!raw) return "var(--accent)";
    if (raw.startsWith("#")) return raw;
    const map: Record<string, string> = {
      RED: "#f87171", PINK: "#f472b6", ORANGE: "#fb923c", YELLOW: "#fbbf24",
      GREEN: "#34d399", BLUE: "#60a5fa", PURPLE: "#a78bfa", GREY: "#9ca3af", GRAY: "#9ca3af",
    };
    return map[raw.toUpperCase()] || "var(--accent)";
  };

  // ── Recipe drawer (W7) ──
  const closeRecipe = () => {
    recipeAbandonedRef.current = true;
    if (recipeSearchTimer.current) clearTimeout(recipeSearchTimer.current);
    setRecipeProduct(null);
    setRecipe(null);
    setRecipeMaterials([]);
    setRecipeError(null);
  };

  const openRecipe = async (p: Product) => {
    recipeAbandonedRef.current = false;
    recipeLoadingForRef.current = p.id;
    setRecipeProduct(p);
    setRecipeLoading(true);
    setRecipe(null);
    setRecipeMaterials([]);
    setRecipeError(null);
    setRecipeSearchQ("");
    setRecipeSearchOpen(false);
    try {
      const data = await api.recipes.get(p.id);
      if (recipeAbandonedRef.current || recipeLoadingForRef.current !== p.id) return;
      setRecipe(data);
      setRecipeMaterials(data.materials ?? []);
    } catch (e: any) {
      if (recipeAbandonedRef.current || recipeLoadingForRef.current !== p.id) return;
      const msg = String(e?.message || e);
      if (msg.includes("404") || msg.toLowerCase().includes("not found") || msg.toLowerCase().includes("no recipe")) {
        // Chưa có công thức — mở trống để user thêm mới
        setRecipe({ item_id: p.id, item_name: p.item_name, is_composite: p.is_composite ?? 0, materials: [], total_cogs: 0, warnings: [] });
        setRecipeMaterials([]);
      } else {
        setRecipeError("Không tải được công thức: " + msg);
      }
    } finally {
      if (recipeLoadingForRef.current === p.id) setRecipeLoading(false);
    }
  };

  const addMaterialToRecipe = (m: RecipeMaterialSearch) => {
    if (recipeMaterials.some((r) => r.material_item_id === m.id)) {
      toast.warning("Nguyên liệu đã có trong công thức");
      setRecipeSearchQ("");
      setRecipeSearchOpen(false);
      return;
    }
    setRecipeMaterials((prev) => [
      ...prev,
      {
        material_item_id: m.id,
        material_name: m.item_name,
        qty_per_unit: 1,
        unit: m.base_unit || "pcs",
        base_unit: m.base_unit,
        unit_cost: m.cost || 0,
        subtotal: m.cost || 0,
        note: "",
      },
    ]);
    setRecipeSearchQ("");
    setRecipeSearchOpen(false);
  };

  const updateMaterialQty = (idx: number, qty: number) => {
    setRecipeMaterials((prev) =>
      prev.map((m, i) =>
        i === idx
          ? { ...m, qty_per_unit: qty, subtotal: qty * m.unit_cost }
          : m
      )
    );
  };

  /** Đổi unit của 1 dòng nguyên liệu trong công thức. */
  const updateMaterialUnit = (idx: number, unit: string) => {
    setRecipeMaterials((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, unit } : m))
    );
  };

  const updateMaterialNote = (idx: number, note: string) => {
    setRecipeMaterials((prev) =>
      prev.map((m, i) => (i === idx ? { ...m, note } : m))
    );
  };

  const removeMaterial = (idx: number) => {
    setRecipeMaterials((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleRecipeSearch = (q: string) => {
    setRecipeSearchQ(q);
    if (recipeSearchTimer.current) clearTimeout(recipeSearchTimer.current);
    if (q.trim().length < 1) {
      setRecipeSearchResults([]);
      setRecipeSearchOpen(false);
      return;
    }
    recipeSearchTimer.current = window.setTimeout(async () => {
      if (recipeAbandonedRef.current) return;
      try {
        const results = await api.recipes.searchMaterials(q.trim());
        if (recipeAbandonedRef.current) return;
        setRecipeSearchResults(results);
        setRecipeSearchOpen(true);
      } catch {
        // giữ kết quả rỗng
      }
    }, 250);
  };

  const saveRecipe = async () => {
    if (!recipeProduct) return;
    setRecipeSaving(true);
    setRecipeError(null);
    try {
      const items = recipeMaterials.map((m) => ({
        material_item_id: m.material_item_id,
        qty_per_unit: m.qty_per_unit,
        unit: m.unit,
        note: m.note,
      }));
      const res = await api.recipes.update(recipeProduct.id, items);
      toast.success(`Đã lưu công thức (${res.count} NL, COGS ${res.total_cogs.toLocaleString("vi-VN")}đ)`);
      setCogsMap((prev) => new Map(prev).set(recipeProduct.id, res.total_cogs ?? 0));
      closeRecipe();
      loadProducts(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi lưu công thức");
    } finally {
      setRecipeSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!form.item_name.trim()) { toast.error("Nhập tên sản phẩm"); return; }
    const validVariants = form.variants.filter((v) => v.default_price > 0 || v.sku.trim());
    if (validVariants.length === 0) { toast.error("Cần ít nhất 1 biến thể với giá bán > 0"); return; }
    setSubmitting(true);
    try {
      const payload: ItemCreate = {
        item_name: form.item_name,
        description: form.description || undefined,
        category_id: form.category_id || undefined,
        image_url: form.image_url || undefined,
        track_stock: form.track_stock,
        color: form.color || undefined,
        is_vat: form.is_vat ? 1 : 0,
        is_bangke: form.is_bangke ? 1 : 0,
        variants: validVariants.map((v) => ({
          variant_name: v.variant_name || undefined,
          sku: v.sku.trim() || slugifySKU(form.item_name, v.variant_name),
          barcode: v.barcode.trim() || genBarcode(),
          default_price: v.default_price,
          cost: v.cost || undefined,
          initial_stock: v.initial_stock || undefined,
          supplier_name: v.supplier_name || undefined,
        })),
      };
      await api.items.create(payload);
      toast.success("Đã tạo sản phẩm");
      setShowCreate(false);
      setForm(emptyForm());
      loadProducts(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi tạo SP");
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = async (id: string) => {
    try {
      const detail = await api.items.detail(id);
      setEditDetail(detail);
      setForm({
        item_name: (detail.item as any).item_name || "",
        description: (detail.item as any).description || "",
        category_id: (detail.item as any).category_id || "",
        image_url: (detail.item as any).image_url || "",
        track_stock: !!(detail.item as any).track_stock,
        color: (detail.item as any).color || "",
        is_vat: !!(detail.variants[0]?.is_vat),
        is_bangke: !!(detail.variants[0]?.is_bangke),
        is_composite: !!(detail.item as any).is_composite,
        variants: detail.variants.map((v) => ({
          variant_id: v.variant_id,
          variant_name: (v as any).variant_name || "",
          sku: v.sku || "",
          barcode: v.barcode || "",
          default_price: v.default_price || 0,
          cost: v.cost || 0,
          initial_stock: v.in_stock || 0,
          supplier_name: "",
        })),
      });
      setShowEdit(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi tải chi tiết SP");
    }
  };

  const handleEdit = async () => {
    if (!editDetail) return;
    if (!form.item_name.trim()) { toast.error("Nhập tên sản phẩm"); return; }
    setSubmitting(true);
    try {
      const payload: ItemUpdate = {
        item_name: form.item_name,
        description: form.description || undefined,
        category_id: form.category_id || undefined,
        image_url: form.image_url || undefined,
        track_stock: form.track_stock,
        color: form.color || undefined,
        is_vat: form.is_vat ? 1 : 0,
        is_bangke: form.is_bangke ? 1 : 0,
        is_composite: form.is_composite ? 1 : 0,
        variants: form.variants.map((v) => ({
          variant_id: v.variant_id,
          variant_name: v.variant_name || undefined,
          sku: v.sku.trim() || slugifySKU(form.item_name, v.variant_name),
          barcode: v.barcode.trim() || undefined,
          default_price: v.default_price,
          cost: v.cost || undefined,
          initial_stock: v.initial_stock,
        })),
      };
      const res = await api.items.update((editDetail.item as any).id, payload);
      const warnings = (res as any).inventory_warnings;
      if (warnings && warnings.length > 0) {
        toast.warning(`Đã lưu local. ${warnings.length} variant chưa sync Loyverse.`);
      } else {
        toast.success("Đã cập nhật sản phẩm");
      }
      setShowEdit(false);
      setEditDetail(null);
      setForm(emptyForm());
      loadProducts(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi cập nhật SP");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!await confirm({ title: "Xoá sản phẩm", message: `Xoá sản phẩm "${name}"?`, danger: true, confirmLabel: "Xoá" })) return;
    try {
      await api.items.delete(id);
      toast.success("Đã xoá sản phẩm");
      mutateProducts((prev) => prev.filter((p) => p.id !== id));
    } catch (e: any) {
      toast.error(e.message || "Lỗi xoá");
    }
  };

  const openHistory = async (id: string) => {
    setHistoryMeta({ id, name: products.find((p) => p.id === id)?.item_name || id });
    setHistoryTab("overview");
    setHistoryLoading(true);
    setShowHistory(id);
    setHistorySales([]);
    setHistoryLots([]);
    setHistoryAudit([]);
    setHistoryOverview(null);
    setHistoryOverviewLoading(true);
    api.items.history(id)
      .then(setHistoryOverview)
      .catch(() => setHistoryOverview(null))
      .finally(() => setHistoryOverviewLoading(false));
    try {
      const detail = await api.items.detail(id);
      const name = (detail.item as any).item_name || "";
      const sku = detail.variants?.[0]?.sku || products.find((p) => p.id === id)?.sku || null;
      setHistoryMeta({ id, name, sku });
      setHistoryAudit(detail.audit_log || []);
      setHistoryLots(detail.stock_lots || []);
      // Prefer SKU for precise match; fallback name
      const q = (sku && String(sku).trim()) || name;
      const to = new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
      const fromD = new Date(Date.now() + 7 * 3600 * 1000);
      fromD.setDate(fromD.getDate() - 365);
      const from = fromD.toISOString().slice(0, 10);
      if (q) {
        const sh = await api.salesHistory({ from, to, q, limit: 100, offset: 0 });
        let rows = sh.rows || [];
        if (sku) {
          const exact = rows.filter((r) => r.sku && r.sku === sku);
          if (exact.length) rows = exact;
        }
        setHistorySales(rows);
      }
      // Enrich lots with import_order when detail.stock_lots thin
      try {
        const lots = await api.itemLots(id);
        if (lots.lots?.length) setHistoryLots(lots.lots);
      } catch { /* keep detail.stock_lots */ }
    } catch {
      setHistoryAudit([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleBulkImport = async () => {
    if (!bulkMd.trim()) return;
    setImporting(true);
    try {
      const result = await api.items.bulkImport(bulkMd);
      toast.success(`Tạo ${result.imported} / ${result.total} sản phẩm.${result.errors.length > 0 ? ` Lỗi: ${result.errors.join(", ")}` : ""}`);
      setShowBulkImport(false);
      setBulkMd("");
      loadProducts(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi import");
    } finally {
      setImporting(false);
    }
  };

  const updateVariant = (idx: number, field: string, value: any) => {
    setForm((prev) => {
      const variants = [...prev.variants];
      variants[idx] = { ...variants[idx], [field]: value };
      return { ...prev, variants };
    });
  };

  const removeVariant = (idx: number) => {
    setForm((prev) => ({ ...prev, variants: prev.variants.filter((_, i) => i !== idx) }));
  };

  /* ─── Inline edit cell helpers ─── */
  const startEdit = (p: Product, field: string, currentValue: any) => {
    if (field !== "item_name" && !p.variant_id) {
      toast.error("Không tìm thấy biến thể, dùng modal Sửa");
      return;
    }
    productEditCancelledRef.current = false;
    setEditingCell({ id: p.id, field, value: String(currentValue ?? ""), variantId: p.variant_id || "" });
  };

  // Sửa Ngày nhập (import_date) → PATCH /api/items, không cần variant. Chuẩn hoá YYYY-MM-DD.
  const startEditDate = (p: Product) => {
    productEditCancelledRef.current = false;
    setEditingCell({ id: p.id, field: "import_date", value: (p.import_date || p.item_created_at || "").slice(0, 10), variantId: p.variant_id || "" });
  };

  const cancelEdit = () => {
    productEditCancelledRef.current = true;
    setEditingCell(null);
  };

  const saveEdit = async () => {
    if (productEditCancelledRef.current) {
      productEditCancelledRef.current = false;
      return;
    }
    if (productEditSavingRef.current || !editingCell) return;
    const { id, field, value, variantId } = editingCell;
    const product = products.find((x) => x.id === id);
    if (!product) { setEditingCell(null); return; }

    // Ngày nhập: lưu thẳng vào items.import_date.
    if (field === "import_date") {
      const cur = (product.import_date || product.item_created_at || "").slice(0, 10);
      if (value === cur) {
        setEditingCell(null);
        return;
      }
      productEditSavingRef.current = true;
      mutateProducts((prev) => prev.map((p) => (p.id === id ? { ...p, import_date: value, item_created_at: value } : p)));
      setEditingCell(null);
      try {
        await api.items.update(id, { import_date: value });
        toast.success("Đã lưu ngày nhập");
      } catch (e: any) {
        toast.error(e.message || "Lỗi cập nhật");
        loadProducts(true);
      } finally {
        productEditSavingRef.current = false;
      }
      return;
    }

    try {
      if (field === "item_name") {
        const itemName = value.trim();
        if (!itemName) {
          setEditingCell(null);
          toast.error("Tên SP không được để trống");
          return;
        }
        if (itemName === product.item_name) {
          setEditingCell(null);
          return;
        }
        productEditSavingRef.current = true;
        mutateProducts((prev) => prev.map((p) => (p.id === id ? { ...p, item_name: itemName } : p)));
        setEditingCell(null);
        await api.items.update(id, { item_name: itemName });
        toast.success("Đã lưu");
      } else if (field === "in_stock") {
        const newStock = Number(value);
        if (Number.isNaN(newStock)) { toast.error("Giá trị tồn không hợp lệ"); return; }
        const oldStock = Number(product.in_stock || 0);
        const delta = newStock - oldStock;
        if (delta === 0) { setEditingCell(null); return; }
        // Optimistic
        productEditSavingRef.current = true;
        mutateProducts((prev) => prev.map((p) => (p.id === id ? { ...p, in_stock: newStock } : p)));
        setEditingCell(null);
        await api.inventoryAdjust({ item_id: id, variant_id: variantId, delta, reason: "inline edit" });
        toast.success("Đã cập nhật tồn");
      } else {
        const variantPayload: any = { variant_id: variantId };
        if (field === "sku") variantPayload.sku = value.trim();
        else if (field === "default_price") variantPayload.default_price = Number(value);
        else if (field === "cost") variantPayload.cost = Number(value);
        else { setEditingCell(null); return; }

        // Optimistic
        productEditSavingRef.current = true;
        mutateProducts((prev) =>
          prev.map((p) =>
            p.id === id
              ? { ...p, [field]: field === "sku" ? variantPayload.sku : Number(value) }
              : p,
          ),
        );
        setEditingCell(null);
        await api.items.update(id, { variants: [variantPayload] });
        toast.success("Đã lưu");
      }
    } catch (e: any) {
      toast.error(e.message || "Lỗi cập nhật");
      loadProducts(true);
    } finally {
      productEditSavingRef.current = false;
    }
  };

  /* ─── Categories CRUD handlers ─── */
  const openCreateCat = () => { setCatForm({ name: "", color: "GREY", has_recipe: false }); setShowCatModal(true); };
  const openEditCat = (c: Category) => { setCatForm({ id: c.id, name: c.name, color: c.color || "GREY", has_recipe: !!c.has_recipe }); setShowCatModal(true); };

  const saveCat = async () => {
    const name = catForm.name.trim();
    if (!name) { toast.error("Nhập tên danh mục"); return; }
    try {
      if (catForm.id) {
        await api.categories.update(catForm.id, { name, color: catForm.color, has_recipe: catForm.has_recipe ? 1 : 0 });
        toast.success("Đã cập nhật danh mục");
      } else {
        await api.categories.create({ name, color: catForm.color, has_recipe: catForm.has_recipe ? 1 : 0 });
        toast.success("Đã tạo danh mục");
      }
      setShowCatModal(false);
      reloadCategories();
    } catch (e: any) {
      toast.error(e.message || "Lỗi lưu danh mục");
    }
  };

  const applyCategorySetChunks = async (
    targets: ReadonlyArray<{ item_id: string }>,
    category_id: string | null,
    token: number,
    tokenRef: { current: number },
    onProgress?: (completed: number, total: number) => void,
  ) => {
    const failures = new Map<string, { item_id: string; error: string; reconciliation_required?: boolean }>();
    if (!targets.length) return [] as Array<{ item_id: string; error: string; reconciliation_required?: boolean }>;
    const parentRequestId = typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `bulk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const chunks = chunkTargets(targets);
    let completed = 0;
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
      const chunk = chunks[chunkIndex];
      const item_ids = chunk.map((target) => target.item_id);
      const suffix = `:${chunkIndex + 1}/${chunks.length}`;
      const request_id = `${parentRequestId.slice(0, 128 - suffix.length)}${suffix}`;
      let result: unknown;
      try {
        result = await api.items.bulkUpdate({ item_ids, operation: "category_set", value: category_id, request_id });
      } catch (error: unknown) {
        if (token !== tokenRef.current) return mergeTargetResults(targets, failures);
        const message = error instanceof Error ? error.message : "Lỗi mạng khi cập nhật danh mục";
        for (const target of targets.slice(completed)) failures.set(target.item_id, { item_id: target.item_id, error: `Chưa xác minh: ${message}` });
        break;
      }
      if (token !== tokenRef.current) return mergeTargetResults(targets, failures);
      const verifiedResponse = validateCategoryBulkResponse(result, { requestId: request_id, targets: item_ids, categoryId: category_id });
      if (!verifiedResponse.ok) {
        const message = `${verifiedResponse.error} — chưa coi là thành công. Mã yêu cầu: ${request_id}`;
        for (const target of targets.slice(completed)) failures.set(target.item_id, { item_id: target.item_id, error: `Chưa xác minh: ${message}` });
        break;
      }
      for (const failure of verifiedResponse.failures) failures.set(failure.item_id, failure);
      completed += chunk.length;
      onProgress?.(completed, targets.length);
    }
    return mergeTargetResults(targets, failures);
  };

  const closeCatDelete = () => {
    if (catDeleteBusyRef.current) return;
    setCatDelete(null);
  };

  const removeCategoryLocally = (id: string) => {
    setCategories((prev) => prev.filter((x) => x.id !== id));
  };

  const deleteCategoryRemote = async (id: string): Promise<boolean> => {
    try {
      await api.categories.delete(id);
      return true;
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 404) return true;
      toast.error(e instanceof Error ? e.message : "Không thể xoá danh mục");
      return false;
    }
  };

  const deleteEmptyCategory = async (c: Category) => {
    if (!await confirm({ title: "Xoá danh mục", message: `Xoá "${c.name}"?`, danger: true, confirmLabel: "Xoá" })) return;
    if (!await deleteCategoryRemote(c.id)) return;
    removeCategoryLocally(c.id);
    toast.success("Đã xoá danh mục");
    reloadCategories();
  };

  const deleteCat = async (c: Category) => {
    if (isProtectedCat(c) || catDeleteBusyRef.current) return;
    try {
      const res = await api.categories.items(c.id);
      if ((res.total || 0) === 0) {
        await deleteEmptyCategory(c);
        return;
      }
      setCatDelete({
        category: c,
        items: res.items || [],
        total: res.total,
        targetId: "",
        failures: [],
        progress: null,
      });
    } catch (e: unknown) {
      if (e instanceof ApiError && e.status === 404) {
        removeCategoryLocally(c.id);
        toast.success("Đã xoá danh mục");
        reloadCategories();
        return;
      }
      toast.error(e instanceof Error ? e.message : "Không tải được sản phẩm danh mục");
    }
  };

  const confirmCatDelete = async () => {
    if (!catDelete || catDeleteBusyRef.current) return;
    const token = ++catDeleteTokenRef.current;
    const category = catDelete.category;
    const destId = catDelete.targetId || null;
    if (destId === category.id) { toast.error("Chọn danh mục khác"); return; }
    catDeleteBusyRef.current = true;
    setCatDeleteBusy(true);
    try {
      const seenMoved = new Set<string>();
      let remaining: CatDeleteItem[] = catDelete.failures.length
        ? catDelete.items.filter((item) => catDelete.failures.some((f) => f.item_id === item.id))
        : catDelete.items;
      while (token === catDeleteTokenRef.current) {
        if (remaining.length === 0) {
          const check = await api.categories.items(category.id);
          if (token !== catDeleteTokenRef.current) return;
          if (check.total === 0) {
            if (!await deleteCategoryRemote(category.id)) return;
            removeCategoryLocally(category.id);
            toast.success("Đã xoá danh mục");
            setCatDelete(null);
            reloadCategories();
            loadProducts(true);
            return;
          }
          remaining = (check.items || []).filter((item) => !seenMoved.has(item.id));
          setCatDelete((prev) => prev && { ...prev, items: remaining, total: check.total, failures: [] });
          if (remaining.length === 0) {
            toast.error(`Còn ${check.total} SP chưa xác minh rỗng. Danh mục chưa xoá.`);
            return;
          }
        }
        const targets = remaining.map((item) => ({ item_id: item.id, item_name: item.item_name, sku: item.sku || "—" }));
        setCatDelete((prev) => prev && { ...prev, progress: { completed: 0, total: targets.length } });
        const failures = await applyCategorySetChunks(
          targets,
          destId,
          token,
          catDeleteTokenRef,
          (completed, total) => setCatDelete((prev) => prev && { ...prev, progress: { completed, total } }),
        );
        if (token !== catDeleteTokenRef.current) return;
        const failedIds = new Set(failures.map((f) => f.item_id));
        for (const item of remaining) if (!failedIds.has(item.id)) seenMoved.add(item.id);
        if (failures.length) {
          const failedItems = remaining.filter((item) => failedIds.has(item.id));
          setCatDelete((prev) => prev && { ...prev, items: failedItems, failures, progress: null });
          toast.error(`Chưa chuyển được ${failures.length} SP. Danh mục chưa xoá.`);
          return;
        }
        remaining = [];
      }
    } catch (e: unknown) {
      if (token !== catDeleteTokenRef.current) return;
      toast.error(e instanceof Error ? e.message : "Không thể xoá danh mục");
    } finally {
      if (token === catDeleteTokenRef.current) {
        catDeleteBusyRef.current = false;
        setCatDeleteBusy(false);
        setCatDelete((prev) => prev && { ...prev, progress: null });
      }
    }
  };

  const COLOR_OPTIONS = ["GREY", "RED", "ORANGE", "YELLOW", "GREEN", "BLUE", "PURPLE"];

  const selectedProducts = useMemo(() => products.filter((p) => selectedIds.has(p.id)), [products, selectedIds]);
  const productSnapshotRef = useRef(new Map<string, Product>());
  useEffect(() => { products.forEach((product) => productSnapshotRef.current.set(product.id, product)); }, [products]);
  const handleVisibleRowsChange = useCallback((rows: Product[]) => {
    const ids = rows.map((p) => p.id);
    setTableVisibleIds((prev) => prev.length === ids.length && prev.every((id, i) => id === ids[i]) ? prev : ids);
  }, []);
  const handleSaleVisibleRowsChange = useCallback((rows: SaleRow[]) => {
    const ids = rows.map((r) => String(r.id));
    setSaleVisibleIds((prev) => (prev.length === ids.length && prev.every((id, i) => id === ids[i]) ? prev : ids));
  }, []);
  const toggleSelected = (id: string) => {
    if (bulkSubmittingRef.current) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const openBulkAction = (action: NonNullable<typeof bulkAction>) => {
    if (selectedIds.size > 100) { toast.error("Tối đa 100 SP/lượt"); return; }
    if (action === "category") {
      setBulkCategoryTargets([...selectedIds].map((item_id) => {
        const product = productSnapshotRef.current.get(item_id);
        return { item_id, item_name: product?.item_name || `SP ${item_id}`, sku: product?.sku || "—", current_category_id: product?.category_id ?? null };
      }));
      setBulkCategoryFailures([]);
    }
    bulkCategoryValueRef.current = "";
    setBulkAction(action);
    setBulkValue("");
    setBulkEndsDate("");
    setBulkMode("fixed");
    setBulkEnabled(true);
    setBulkSaleAction("create");
  };
  const closeBulkAction = () => {
    if (bulkSubmittingRef.current) return;
    setBulkAction(null);
    setBulkCategoryTargets([]);
    setBulkCategoryFailures([]);
  };

  /* ═══ Gộp sản phẩm — preview + guarded commit ═══ */
  const closeMerge = () => {
    mergeSeqRef.current++;
    setMergeOpen(false);
    setMergePair(null);
    setMergeCanonicalId(null);
    setMergePreview(null);
    setMergeError(null);
    setMergeLoading(false);
    setMergeAcks({ sales_stopped: false, devices_synced: false, promo: false });
    // mergeOperation / mergeOutcome stay: an unknown result must keep its id.
    setMergeStatus(null);
  };
  const openMerge = () => {
    if (!mergeAllowed || bulkSubmittingRef.current) return;
    const pair = [...selectedIds];
    if (pair.length !== 2) { toast.error("Chọn đúng 2 sản phẩm để gộp"); return; }
    mergeSeqRef.current++;
    setMergeOpen(true);
    setMergePair([pair[0], pair[1]]);
    // No default: the owner must explicitly pick which product survives.
    setMergeCanonicalId(null);
    setMergePreview(null);
    setMergeError(null);
    setMergeOutcome(null);
    setMergeStatus(null);
    setMergeAcks({ sales_stopped: false, devices_synced: false, promo: false });
  };
  useEffect(() => {
    if (!mergeOpen || !mergePair || !mergeCanonicalId) return;
    const aliasId = mergePair[0] === mergeCanonicalId ? mergePair[1] : mergePair[0];
    const seq = ++mergeSeqRef.current;
    setMergeLoading(true);
    setMergeError(null);
    setMergePreview(null);
    setMergeOutcome(null);
    setMergeStatus(null);
    // A new preview invalidates every maintenance acknowledgement.
    setMergeAcks({ sales_stopped: false, devices_synced: false, promo: false });
    api.mergePreview(mergeCanonicalId, aliasId)
      .then((preview) => {
        if (seq !== mergeSeqRef.current) return;
        setMergePreview(preview);
      })
      .catch((e: any) => {
        if (seq !== mergeSeqRef.current) return;
        setMergePreview(null);
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          setMergeError("Máy chủ không cấp quyền gộp sản phẩm cho tài khoản này.");
          loadMergeCapability();
        } else {
          setMergeError(e?.message || "Không tải được bản xem trước");
        }
      })
      .finally(() => {
        if (seq === mergeSeqRef.current) setMergeLoading(false);
      });
  }, [mergeOpen, mergePair, mergeCanonicalId, mergeReloadKey]);

  const mergeCanonicalPromo = !!mergePreview?.canonical.active_sales.length;
  const mergeBlockedReason = mergeExecutionBlockedReason(
    mergePreview
      ? {
          compatible: mergePreview.compatible,
          execution_available: mergePreview.execution_available,
          capability: mergePreview.capability,
          execution_token: mergePreview.execution_token,
          canonicalPromo: mergeCanonicalPromo,
        }
      : null,
  );
  const mergeAckReady = mergeAcksSatisfied(mergeAcks, mergeCanonicalPromo);
  const mergePairConflict =
    mergePair && mergeCanonicalId
      ? mergePendingPairConflict(
          mergeOperation,
          mergeCanonicalId,
          mergePair[0] === mergeCanonicalId ? mergePair[1] : mergePair[0],
        )
      : null;
  const mergeSubmitDisabledReason = !mergeAllowed
    ? "Tài khoản hiện tại không có quyền gộp sản phẩm."
    : mergeRestored
      ? "Thao tác từ phiên trước chưa xác định — kiểm tra trạng thái trước."
      : mergePairConflict
        ? mergePairConflict
        : !mergeCanonicalId
          ? "Chọn sản phẩm chính."
          : mergeBlockedReason
            ? mergeBlockedReason
            : !mergeAckReady
              ? "Chưa xác nhận đủ điều kiện tạm dừng bán và đồng bộ thiết bị."
              : mergeOutcome?.kind === "active"
                ? "Đã gộp xong."
                : mergeOutcome?.kind === "needs_reconciliation"
                  ? "Kết quả chưa xác định — cần đối soát, không gửi lại."
                  : mergeOutcome?.kind === "disabled"
                    ? "Máy chủ chưa cho phép gộp — kiểm tra trạng thái với cùng mã."
                    : mergeOutcome?.kind === "unknown"
                      ? "Chưa rõ kết quả — kiểm tra trạng thái trước."
                      : mergeOutcome?.kind === "pending"
                        ? "Thao tác đang chờ xác nhận — kiểm tra trạng thái trước."
                        : mergeOutcome?.kind === "failed"
                          ? "Thao tác báo lỗi chưa rõ — kiểm tra trạng thái trước khi gửi lại."
                          : null;

  const clearMergePointer = (operationId: string) => {
    // Clear only the exact pointer we own. LocalStorage has no CAS: another tab may
    // have written a newer operation between our request and this response, and
    // that newer pointer must survive.
    const target = mergeClearTargetRef.current;
    const scope = target?.operationId === operationId ? target.scope : mergeScopeRef.current;
    clearStoredMergeOperation(scope, operationId, undefined, currentMergeLegacyStorageScope());
    mergeClearTargetRef.current = null;
  };

  const applyMergeOutcome = (outcome: MergeOutcomeClass) => {
    setMergeOutcome(outcome);
    const operationId = mergeClearTargetRef.current?.operationId ?? mergeOperation?.operationId ?? null;
    if (outcome.kind === "active") {
      toast.success("Đã gộp sản phẩm");
      if (operationId) clearMergePointer(operationId);
      setMergeOperation(null);
      setMergeRestored(false);
      setSelectedIds(new Set());
      loadProducts(true);
      api.inventoryStats().then(setStats).catch(() => {});
    } else if (!outcome.keepOperationId) {
      if (operationId) clearMergePointer(operationId);
      setMergeOperation(null);
      setMergeRestored(false);
    } else if (outcome.kind === "retryable") {
      // "prepared" is the only replayable phase: retry stays deliberate and needs
      // fresh explicit acknowledgements; the server still validates the token.
      setMergeAcks({ sales_stopped: false, devices_synced: false, promo: false });
    }
  };

  const submitMerge = async () => {
    if (mergeSubmittingRef.current) return;
    // Fail closed even if the modal was opened before the capability changed.
    if (!mergeAllowed) {
      toast.error("Tài khoản hiện tại không có quyền gộp sản phẩm.");
      return;
    }
    if (!mergePreview || !mergePair || !mergeCanonicalId) return;
    if (mergeBlockedReason) { toast.error(mergeBlockedReason); return; }
    const aliasId = mergePair[0] === mergeCanonicalId ? mergePair[1] : mergePair[0];
    // Persist the recovery pointer BEFORE sending. No pointer = no request, so a
    // reload can never lose an in-flight operation id. prepareMergeCommit re-reads
    // storage: a stale React copy or another tab's pointer blocks the send.
    const scope = currentMergeStorageScope();
    const legacyScope = currentMergeLegacyStorageScope();
    const prepared = prepareMergeCommit({
      pending: mergeOperation,
      canonicalItemId: mergeCanonicalId,
      aliasItemId: aliasId,
      previewToken: mergePreview.execution_token?.preview_token || "",
      canonicalPromo: mergeCanonicalPromo,
      acks: mergeAcks,
      storeId: mergePreview.store_id,
      scope,
      legacyScope,
    });
    if (!prepared.ok) { toast.error(prepared.reason); if (!mergeOperation) {
      const snapshot = readMergeStorageSnapshot(scope, undefined, legacyScope);
      const stored = snapshot.operations[0] ?? null;
      if (stored) { setMergeOperation(stored); setMergeRestored(true); }
    } return; }
    mergeClearTargetRef.current = { scope, operationId: prepared.operation.operationId };
    setMergeOperation(prepared.operation);
    setMergeRestored(false);
    // A restored pointer was already reconciled by the owner; a prepared replay
    // still needs explicit acknowledgements, which the preview reset guarantees.
    mergeSubmittingRef.current = true;
    setMergeSubmitting(true);
    setMergeOutcome(null);
    setMergeStatus(null);
    try {
      const response = await api.mergeCommit(prepared.body);
      const status = (response as any)?.status ?? ((response as any)?.phase ? (response as MergeStatus) : null);
      if (status) setMergeStatus(status as MergeStatus);
      applyMergeOutcome(classifyMergePayload(response));
    } catch (e: any) {
      const responseStatus = e?.payload?.status;
      if (responseStatus && typeof responseStatus.phase === "string") setMergeStatus(responseStatus as MergeStatus);
      let outcome = classifyMergeFailure(e);
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        // Keep the durable id: a refused request proves nothing about an earlier one.
        outcome = {
          kind: "failed",
          keepOperationId: true,
          message: "Máy chủ từ chối quyền gộp sản phẩm. Mã thao tác được giữ lại để kiểm tra sau khi đăng nhập.",
        };
        loadMergeCapability();
      }
      applyMergeOutcome(outcome);
      toast.error(outcome.message);
    } finally {
      mergeSubmittingRef.current = false;
      setMergeSubmitting(false);
    }
  };

  const checkMergeStatus = async () => {
    const operationId = mergeOperation?.operationId;
    if (!operationId || mergeStatusLoadingRef.current) return;
    mergeStatusLoadingRef.current = true;
    setMergeStatusLoading(true);
    try {
      const status = await api.mergeStatus(operationId);
      setMergeStatus(status);
      const outcome = classifyMergeStatus(status);
      // The pointer has now been reconciled with the server; further retries still
      // need a fresh preview + explicit acknowledgements.
      setMergeRestored(false);
      if (outcome.kind !== "unknown") applyMergeOutcome(outcome);
      else setMergeOutcome(outcome);
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 404) {
        // Nothing was recorded under this id: resending the SAME id is safe.
        setMergeRestored(false);
        setMergeOutcome({
          kind: "retryable",
          keepOperationId: true,
          message: "Máy chủ chưa ghi nhận mã thao tác này. Có thể gửi lại với cùng mã sau khi tải lại bản xem trước.",
        });
      } else if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
        // Status lookup itself was refused: keep the operation id and let the owner
        // re-authenticate instead of discarding an unresolved merge.
        setMergeOutcome({
          kind: "unknown",
          keepOperationId: true,
          message: "Máy chủ không cấp quyền kiểm tra thao tác gộp. Đăng nhập lại rồi kiểm tra với cùng mã.",
        });
        loadMergeCapability();
      } else {
        const outcome = classifyMergeFailure(e);
        setMergeOutcome(outcome);
      }
    } finally {
      mergeStatusLoadingRef.current = false;
      setMergeStatusLoading(false);
    }
  };

  const finishBulk = async (itemIds: string[], request: Promise<{ updated: number; failed: Array<{ item_id: string; error: string }> }>) => {
    bulkSubmittingRef.current = true;
    setBulkSubmitting(true);
    try {
      const result = await request;
      const failedIds = new Set(result.failed.map((f) => f.item_id));
      setSelectedIds(failedIds);
      if (result.failed.length) toast.error(`Không cập nhật được ${result.failed.length}/${itemIds.length}: ${result.failed.map((f) => f.item_id).join(", ")}`);
      else toast.success(`Đã cập nhật ${result.updated}/${itemIds.length}`);
      setBulkAction(null);
      loadProducts(true);
    } catch (e: any) {
      toast.error(e.message || "Lỗi cập nhật hàng loạt");
    } finally {
      bulkSubmittingRef.current = false;
      setBulkSubmitting(false);
    }
  };
  const submitBulkCategory = async () => {
    if (bulkSubmittingRef.current || !bulkCategoryTargets.length) return;
    const token = ++bulkCategoryRequestTokenRef.current;
    const frozenTargets = Object.freeze(bulkCategoryTargets.map((target) => Object.freeze({ ...target })));
    const category_id = bulkCategoryValueRef.current || null;
    const parentRequestId = typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `bulk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const chunks = chunkTargets(frozenTargets);
    const failures = new Map<string, { item_id: string; error: string; reconciliation_required?: boolean }>();
    let completed = 0;
    bulkSubmittingRef.current = true;
    setBulkSubmitting(true);
    setBulkCategoryFailures([]);
    setBulkCategoryProgress({ completed, total: frozenTargets.length });
    try {
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
        const chunk = chunks[chunkIndex];
        const item_ids = chunk.map((target) => target.item_id);
        const suffix = `:${chunkIndex + 1}/${chunks.length}`;
        const request_id = `${parentRequestId.slice(0, 128 - suffix.length)}${suffix}`;
        let result: unknown;
        try {
          result = await api.items.bulkUpdate({ item_ids, operation: "category_set", value: category_id, request_id });
        } catch (error: unknown) {
          if (token !== bulkCategoryRequestTokenRef.current) return;
          const message = error instanceof Error ? error.message : "Lỗi mạng khi cập nhật danh mục";
          for (const target of frozenTargets.slice(completed)) failures.set(target.item_id, { item_id: target.item_id, error: `Chưa xác minh: ${message}` });
          break;
        }
        if (token !== bulkCategoryRequestTokenRef.current) return;
        const verifiedResponse = validateCategoryBulkResponse(result, { requestId: request_id, targets: item_ids, categoryId: category_id });
        if (!verifiedResponse.ok) {
          const message = `${verifiedResponse.error} — chưa coi là thành công. Mã yêu cầu: ${request_id}`;
          for (const target of frozenTargets.slice(completed)) failures.set(target.item_id, { item_id: target.item_id, error: `Chưa xác minh: ${message}` });
          break;
        }
        for (const failure of verifiedResponse.failures) failures.set(failure.item_id, failure);
        const patches = new Map(verifiedResponse.successes.map((success) => [success.item_id, {
          category_id: success.verified_value,
          category_name: success.verified_value ? catMap[success.verified_value]?.name || null : null,
        }]));
        if (patches.size) {
          mutateProducts((current) => current.map((row) => {
            const patch = patches.get(row.id);
            return patch ? { ...row, ...patch } : row;
          }));
          for (const [id, patch] of patches) {
            const snap = productSnapshotRef.current.get(id);
            if (snap) productSnapshotRef.current.set(id, { ...snap, ...patch });
          }
          setSelectedIds((current) => {
            const next = new Set(current);
            for (const id of patches.keys()) next.delete(id);
            return next;
          });
        }
        completed += chunk.length;
        setBulkCategoryProgress({ completed, total: frozenTargets.length });
      }
      if (token !== bulkCategoryRequestTokenRef.current) return;
      const orderedFailures = mergeTargetResults(frozenTargets, failures);
      if (orderedFailures.length) {
        const failedIds = new Set(orderedFailures.map((failure) => failure.item_id));
        setBulkCategoryTargets(frozenTargets.filter((target) => failedIds.has(target.item_id)));
        setBulkCategoryFailures(orderedFailures);
        toast.error(`Chưa cập nhật được ${orderedFailures.length}/${frozenTargets.length} SP`);
        return;
      }
      toast.success(`Đã đổi danh mục ${frozenTargets.length}/${frozenTargets.length} SP`);
      setBulkAction(null);
      setBulkCategoryTargets([]);
      setBulkCategoryFailures([]);
      setSelectedIds((current) => {
        const next = new Set(current);
        frozenTargets.forEach((target) => next.delete(target.item_id));
        return next;
      });
      loadProducts(true);
    } finally {
      if (token === bulkCategoryRequestTokenRef.current) {
        bulkSubmittingRef.current = false;
        setBulkSubmitting(false);
        setBulkCategoryProgress(null);
      }
    }
  };
  const submitBulkAction = async () => {
    if (bulkSubmittingRef.current) return;
    const action = bulkAction;
    if (action === "category") return submitBulkCategory();
    const item_ids = [...selectedIds];
    if (!item_ids.length || !action) return;
    if (item_ids.length > 100) { toast.error("Tối đa 100 SP/lượt"); return; }
    if (action === "vat" || action === "bangke") return finishBulk(item_ids, api.items.bulkUpdate({ item_ids, operation: action === "vat" ? "vat_set" : "bangke_set", value: bulkEnabled }));
    if (action === "import_date") return finishBulk(item_ids, api.items.bulkUpdate({ item_ids, operation: "import_date_set", value: bulkValue || null }));
    if (action === "price") {
      const value = Number(bulkValue);
      if (!Number.isFinite(value) || (bulkMode === "fixed" && (!Number.isInteger(value) || value < 0))) { toast.error("Nhập giá VND nguyên ≥ 0 hoặc phần trăm hợp lệ"); return; }
      return finishBulk(item_ids, api.items.bulkUpdate({ item_ids, operation: bulkMode === "fixed" ? "price_set" : "price_percent", value: bulkMode === "fixed" ? value : Math.round(value) }));
    }
    if (action === "sale") {
      if (bulkSaleAction === "end" && !await confirm({ title: "Kết thúc sale", message: `Kết thúc sale cho ${item_ids.length} sản phẩm? Giá sẽ về giá gốc.`, danger: true, confirmLabel: "Kết thúc" })) return;
      const ends_at = bulkEndsDate ? dateOnlyEndToIso(bulkEndsDate) : null;
      if (bulkEndsDate && !ends_at) { toast.error("Ngày hết hạn không hợp lệ"); return; }
      const value = Number(bulkValue);
      if (bulkSaleAction === "create" && (!Number.isFinite(value) || value < 0)) { toast.error("Nhập giá sale hoặc % giảm"); return; }
      return finishBulk(item_ids, api.productSales.bulk({ item_ids, action: bulkSaleAction, ...(bulkSaleAction === "create" ? { mode: bulkMode, value: Math.round(value), ends_at } : { ends_at }) }));
    }
  };
  const exportSelectedCsv = () => {
    const quote = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = selectedProducts.map((p) => [p.item_name, p.sku, p.category_name || catMap[p.category_id || ""]?.name || "Không phân loại", p.default_price, p.cost, p.in_stock, p.import_date || p.item_created_at || "", p.is_sale ? "Đang sale" : ""]);
    const csv = ["Tên,SKU,Danh mục,Giá bán,Giá vốn,Tồn,Ngày nhập,Sale", ...rows.map((r) => r.map(quote).join(","))].join("\r\n");
    const url = URL.createObjectURL(new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a"); a.href = url; a.download = "san-pham-da-chon.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const setInlineCategory = async (p: Product, value: string) => {
    const previousCategory = { category_id: p.category_id, category_name: p.category_name };
    const category_id = value || null;
    const nextCategory = { category_id, category_name: value ? catMap[value]?.name || null : null };
    const token = ++categoryMutationSequenceRef.current;
    const request_id = typeof globalThis.crypto?.randomUUID === "function"
      ? globalThis.crypto.randomUUID()
      : `category-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    categoryMutationTokensRef.current.set(p.id, token);
    const isLatest = () => categoryMutationTokensRef.current.get(p.id) === token;
    const finish = () => { if (isLatest()) categoryMutationTokensRef.current.delete(p.id); };
    const rollback = () => mutateProducts((prev) => prev.map((row) =>
      row.id === p.id ? { ...row, ...previousCategory } : row,
    ));
    const fail = (message: string) => {
      rollback();
      toast.error(message);
      loadProducts(true, isLatest).finally(finish);
    };

    setEditingCategoryId(null);
    mutateProducts((prev) => prev.map((row) => row.id === p.id ? { ...row, ...nextCategory } : row));
    try {
      const result = await api.items.bulkUpdate({ item_ids: [p.id], operation: "category_set", value: category_id, request_id });
      if (!isLatest()) return;
      const verifiedResponse = validateCategoryBulkResponse(result, { requestId: request_id, targets: [p.id], categoryId: category_id });
      if (!verifiedResponse.ok) return fail(`${verifiedResponse.error} — chưa coi là thành công. Mã yêu cầu: ${request_id}`);
      const failure = verifiedResponse.failures[0];
      if (failure) return fail(`${failure.error}${failure.reconciliation_required ? " Cần đối soát." : ""}`);
      const verified = verifiedResponse.successes[0];
      if (!verified) return fail("Phản hồi thiếu xác minh danh mục");
      const confirmed = {
        category_id: verified.verified_value,
        category_name: verified.verified_value ? catMap[verified.verified_value]?.name || null : null,
      };
      const snap = productSnapshotRef.current.get(p.id);
      if (snap) productSnapshotRef.current.set(p.id, { ...snap, ...confirmed });
      mutateProducts((current) => current.map((row) => row.id === p.id ? { ...row, ...confirmed } : row));
      toast.success("Đã cập nhật danh mục");
      finish();
    } catch (error: unknown) {
      if (!isLatest()) return;
      fail(error instanceof Error ? error.message : "Lỗi cập nhật danh mục");
    }
  };

  /* ─── Form sections (POS-professional) ─── */
  const renderFormBody = () => (
    <>
      {/* Section: Cơ bản */}
      <div className="form-section">
        <div className="form-section-title">
          <span className="material-symbols-outlined">inventory_2</span>
          Thông tin cơ bản
        </div>
        <div className="form-group">
          <label>Tên sản phẩm <span className="required">*</span></label>
          <input
            placeholder="Ví dụ: Áo thun cotton"
            value={form.item_name}
            onChange={(e) => setForm({ ...form, item_name: e.target.value })}
            autoFocus
          />
        </div>
        <div className="form-group">
          <label>Mô tả</label>
          <textarea
            placeholder="Chất liệu, kích thước, ghi chú nội bộ..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Danh mục</label>
            <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <option value="">— Không nhóm —</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Màu nhãn</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="color"
                value={form.color || "#60a5fa"}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                style={{ width: 38, height: 36, padding: 2, borderRadius: 6 }}
              />
              <input
                placeholder="#60a5fa"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                style={{ flex: 1 }}
              />
            </div>
          </div>
        </div>
        <div className="form-group">
          <label>Hình ảnh</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {(imagePreview || form.image_url) && (
              <img src={imagePreview || form.image_url} alt="preview" className="img-preview" style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 8 }} />
            )}
            <FilePicker
              variant="button"
              accept="image/*"
              label={imagePreview || form.image_url ? "Đổi ảnh" : "Chọn ảnh"}
              icon="image"
              busy={imageUploading}
              onSelect={async (file) => {
                if (!file) return;
                // Preview immediately
                setImagePreview(URL.createObjectURL(file));
                // If editing existing product, upload now
                if (editDetail) {
                  setImageUploading(true);
                  try {
                    const res = await api.productImages.upload(editDetail.item.id, file);
                    if (res.ok && res.url) {
                      setForm({ ...form, image_url: res.url });
                      toast.success("Đã upload ảnh");
                    } else {
                      toast.error(res.error || "Upload thất bại");
                    }
                  } catch (err: any) {
                    toast.error(err.message || "Upload thất bại");
                  } finally {
                    setImageUploading(false);
                  }
                }
              }}
            />
            {(imagePreview || form.image_url) && (
              <button
                type="button"
                style={{ padding: "6px 10px", borderRadius: 6, background: "var(--danger)", color: "#fff", border: "none", fontSize: 13, cursor: "pointer" }}
                onClick={async () => {
                  if (editDetail) {
                    const imgs = await api.productImages.list(editDetail.item.id);
                    if (imgs.images?.length) {
                      await api.productImages.delete(imgs.images[0].id);
                    }
                  }
                  setForm({ ...form, image_url: "" });
                  setImagePreview(null);
                  toast.success("Đã xoá ảnh");
                }}
              >Xoá ảnh</button>
            )}
          </div>
        </div>
      </div>

      {/* Section: Thuế & quản lý */}
      <div className="form-section">
        <div className="form-section-title">
          <span className="material-symbols-outlined">receipt_long</span>
          Thuế & quản lý kho
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={form.track_stock}
              onChange={(e) => setForm({ ...form, track_stock: e.target.checked })}
            />
            Theo dõi tồn kho
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={form.is_vat}
              onChange={(e) => setForm({ ...form, is_vat: e.target.checked })}
            />
            Có VAT (xuất hoá đơn đỏ)
          </label>
          <label className="form-checkbox">
            <input
              type="checkbox"
              checked={form.is_bangke}
              onChange={(e) => setForm({ ...form, is_bangke: e.target.checked })}
            />
            Bảng kê 01/TNDN (hàng VN không VAT)
          </label>
        </div>
        <div className="form-hint">Đánh dấu để đưa SP vào kho ảo và sổ S1a tự động.</div>
        <label className="form-checkbox" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
          <input type="checkbox" checked={form.is_composite}
            onChange={(e) => setForm({ ...form, is_composite: e.target.checked })} />
          SP sản xuất (có công thức nguyên liệu → tính COGS)
        </label>
        {form.is_composite && editDetail && (
          <button type="button" className="btn btn-ghost"
            onClick={() => openRecipe(editDetail.item as Product)}
            style={{ marginTop: 8, color: "var(--accent-blue)" }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>science</span>
            Mở công thức
          </button>
        )}
      </div>

      {/* Section: Variants */}
      <div className="form-section">
        <div className="form-section-title" style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="material-symbols-outlined">tune</span>
            Biến thể & giá ({form.variants.length})
          </span>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: "4px 10px" }}
            onClick={() => setForm({ ...form, variants: [...form.variants, emptyVariant()] })}
            type="button"
          >
            + Thêm biến thể
          </button>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table className="variant-table">
            <thead>
              <tr>
                <th>Tên BT</th>
                <th>SKU</th>
                <th>Barcode</th>
                <th className="num">Giá vốn</th>
                <th className="num">Giá bán *</th>
                <th className="num">Tồn kho</th>
                <th>NCC</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {form.variants.map((v, idx) => (
                <tr key={idx}>
                  <td data-label="Tên BT"><input placeholder="Đỏ / Size M" value={v.variant_name} onChange={(e) => updateVariant(idx, "variant_name", e.target.value)} /></td>
                  <td data-label="SKU"><input placeholder="SKU" value={v.sku} onChange={(e) => updateVariant(idx, "sku", e.target.value)} /></td>
                  <td data-label="Barcode"><input placeholder="Barcode" value={v.barcode} onChange={(e) => updateVariant(idx, "barcode", e.target.value)} /></td>
                  <td data-label="Giá vốn"><input type="number" inputMode="numeric" className="num" placeholder="0" value={v.cost || ""} onChange={(e) => updateVariant(idx, "cost", Number(e.target.value))} /></td>
                  <td data-label="Giá bán *"><input type="number" inputMode="numeric" className="num" placeholder="0" value={v.default_price || ""} onChange={(e) => updateVariant(idx, "default_price", Number(e.target.value))} /></td>
                  <td data-label="Tồn kho"><input type="number" inputMode="numeric" className="num" placeholder="0" value={v.initial_stock || ""} onChange={(e) => updateVariant(idx, "initial_stock", Number(e.target.value))} /></td>
                  <td data-label="NCC"><input placeholder="Tên NCC" value={v.supplier_name} onChange={(e) => updateVariant(idx, "supplier_name", e.target.value)} /></td>
                  <td data-label="">
                    <button
                      className="btn-remove"
                      onClick={() => removeVariant(idx)}
                      type="button"
                      title="Xoá biến thể"
                      disabled={form.variants.length <= 1}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ─── Product table columns (inline edit) ─── */
  const renderEditableCell = (
    p: Product,
    field: "item_name" | "sku" | "default_price" | "cost" | "in_stock",
    display: React.ReactNode,
    type: "text" | "number" = "text",
  ) => {
    const isEditing = editingCell?.id === p.id && editingCell?.field === field;
    if (isEditing) {
      return (
        <input
          className="form-input"
          autoFocus
          type={type}
          inputMode={type === "number" ? "numeric" : undefined}
          value={editingCell!.value}
          onChange={(e) => setEditingCell({ ...editingCell!, value: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveEdit();
            else if (e.key === "Escape") cancelEdit();
          }}
          onBlur={saveEdit}
          style={{ width: "100%", padding: "4px 8px", fontSize: 13, minHeight: 36 }}
        />
      );
    }
    const cur = (p as any)[field];
    return (
      <div
        onClick={() => startEdit(p, field, cur)}
        style={{ cursor: "pointer", padding: "8px 4px", minHeight: 36, borderRadius: 4 }}
        title="Click để sửa"
      >
        {display}
      </div>
    );
  };

  const tableColumns: Column<Product>[] = useMemo(() => [
    ...(selectionMode ? [{
      key: "select",
      header: (
        <SaleCheckbox
          label="Chọn trang hiện tại"
          disabled={bulkSubmitting}
          checked={tableVisibleIds.length > 0 && tableVisibleIds.every((id) => selectedIds.has(id))}
          onToggle={() => setSelectedIds((prev) => {
            const next = new Set(prev);
            const allSelected = tableVisibleIds.length > 0 && tableVisibleIds.every((id) => next.has(id));
            for (const id of tableVisibleIds) allSelected ? next.delete(id) : next.add(id);
            return next;
          })}
        />
      ),
      width: 44,
      render: (p: Product) => <SaleCheckbox label={`Chọn ${p.item_name}`} checked={selectedIds.has(p.id)} onToggle={() => toggleSelected(p.id)} />,
    } as Column<Product>] : []),
    {
      key: "item_name",
      header: "Tên SP",
      sortable: true,
      render: (p) => renderEditableCell(
        p,
        "item_name",
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{ fontWeight: 500, fontSize: 13 }}>{p.item_name}</span>
          {p.description && <span style={{ fontSize: 11, opacity: 0.6 }}>{p.description}</span>}
        </div>,
      ),
    },
    {
      key: "category_name",
      header: "Danh mục",
      sortable: true,
      render: (p) => {
        const cat = p.category_id ? catMap[p.category_id] : null;
        if (editingCategoryId === p.id) return (
          <select autoFocus aria-label={`Danh mục ${p.item_name}`} value={p.category_id || ""} onChange={(e) => {
            if (e.target.value === "__create__") { openCreateCat(); setEditingCategoryId(null); return; }
            setInlineCategory(p, e.target.value);
          }} onBlur={() => setEditingCategoryId(null)} style={{ minHeight: 36, maxWidth: 180 }}>
            <option value="">Không phân loại</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            <option value="__create__">+ Tạo danh mục</option>
          </select>
        );
        return <button className="btn btn-ghost" onClick={() => setEditingCategoryId(p.id)} title="Sửa danh mục" style={{ minHeight: 36, padding: "4px 6px", fontSize: 12 }}>
          {cat && <span style={{ background: colorOf(cat.color), width: 8, height: 8, borderRadius: "50%", display: "inline-block", marginRight: 6 }} />}
          {p.category_name || cat?.name || "—"}
        </button>;
      },
    },
    { key: "sku", header: "SKU", sortable: true, render: (p) => renderEditableCell(p, "sku", <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{p.sku || "—"}</span>) },
    { key: "default_price", header: "Giá bán", align: "right", sortable: true, render: (p) => renderEditableCell(p, "default_price", <span style={{ fontWeight: 500 }}>{p.default_price != null ? formatVND(p.default_price) : "—"}</span>, "number") },
    { key: "cost", header: "Giá vốn", align: "right", sortable: true, render: (p) => {
      if (hasRecipe(p)) {
        const cogs = cogsMap.get(p.id);
        return <span style={{ color: cogs != null ? "var(--accent-blue)" : undefined, cursor: "pointer" }} onClick={() => openRecipe(p)} title={cogs != null ? "Giá vốn = COGS công thức (bấm xem/sửa)" : "Đang tải COGS…"}>{cogs != null ? formatVND(cogs) + "đ" : "…"}</span>;
      }
      return renderEditableCell(p, "cost", <span>{p.cost != null ? formatVND(p.cost) : "—"}</span>, "number");
    } },
    {
      key: "cogs",
      header: "COGS",
      align: "right",
      sortable: true,
      render: (p) => {
        if (!hasRecipe(p)) return <span style={{ opacity: 0.3 }}>—</span>;
        const cogs = cogsMap.get(p.id);
        return (
          <span
            style={{ cursor: "pointer", color: "var(--accent-blue)" }}
            onClick={() => openRecipe(p)}
            title={cogs != null ? "Bấm xem/sửa công thức" : "Đang tải COGS…"}
          >
            {cogs != null ? formatVND(cogs) + "đ" : "…"}
          </span>
        );
      },
    },
    {
      key: "margin",
      header: "Margin",
      align: "right",
      render: (p) => {
        const price = Number(p.default_price || 0);
        // SP sản xuất → dùng COGS (công thức) thay vì p.cost (giá vốn nhập = 0)
        const cogs = hasRecipe(p) ? cogsMap.get(p.id) : undefined;
        const cost = cogs != null ? Number(cogs) : Number(p.cost || 0);
        if (!price || !cost) return <span style={{ opacity: 0.4 }}>—</span>;
        const m = ((price - cost) / price) * 100;
        const color = m < 10 ? "var(--accent-red)" : m < 30 ? "var(--accent-orange)" : "var(--accent-green)";
        return <span style={{ color, fontWeight: 500, fontSize: 12 }}>{m.toFixed(1)}%</span>;
      },
    },
    {
      key: "in_stock",
      header: "Tồn",
      align: "right",
      sortable: true,
      render: (p) => {
        const stock = Number(p.in_stock || 0);
        const variant: "ok" | "warn" | "bad" = stock <= 0 ? "bad" : stock <= 5 ? "warn" : "ok";
        return renderEditableCell(p, "in_stock", <Badge variant={variant}>{stock}</Badge>, "number");
      },
    },
    {
      key: "item_created_at", header: "Ngày nhập", sortable: true,
      render: (p) => {
        const isEditing = editingCell?.id === p.id && editingCell?.field === "import_date";
        if (isEditing) {
          return (
            <input
              className="form-input"
              autoFocus
              type="date"
              value={editingCell!.value}
              onChange={(e) => setEditingCell({ ...editingCell!, value: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEdit();
                else if (e.key === "Escape") cancelEdit();
              }}
              onBlur={saveEdit}
              style={{ width: "100%", padding: "4px 8px", fontSize: 13, minHeight: 36 }}
            />
          );
        }
        return (
          <div
            onClick={() => startEditDate(p)}
            style={{ cursor: "pointer", padding: "8px 4px", minHeight: 36, borderRadius: 4 }}
            title="Click để sửa ngày nhập"
          >
            {p.item_created_at ? formatDate(p.item_created_at) : "—"}
          </div>
        );
      },
    },
    { key: "stock_updated_at", header: "Cập nhật", sortable: true, render: (p) => p.stock_updated_at ? formatDate(p.stock_updated_at) : "—" },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (p) => (
        <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          {hasRecipe(p) && (
            <button className="btn btn-ghost" style={{ padding: "4px 6px", color: "var(--accent-blue)" }} onClick={() => openRecipe(p)} title="Xem/sửa công thức">
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>science</span>
            </button>
          )}
          <button className="btn btn-ghost" style={{ padding: "4px 6px" }} onClick={() => openEdit(p.id)} title="Sửa sản phẩm">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
          </button>
          <button className="btn btn-ghost" style={{ padding: "4px 6px" }} onClick={() => openHistory(p.id)} title="Lịch sử bán / nhập">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>history</span>
          </button>
          <button className="btn btn-ghost" style={{ padding: "4px 6px", color: "var(--accent-red)" }} onClick={() => handleDelete(p.id, p.item_name)} title="Xóa sản phẩm (gỡ cả trên Loyverse)">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
          </button>
          <button className="btn btn-ghost" style={{ padding: "4px 6px" }} onClick={() => handleAddToPrint([p])} title="Thêm in tem"><span className="material-symbols-outlined" style={{ fontSize: 16 }}>print</span></button>
        </div>
      ),
    },
  ], [editingCell, editingCategoryId, catMap, categories, products, selectionMode, selectedIds, tableVisibleIds, handleAddToPrint]);

  return (
    <GlassPanel title="Master sản phẩm" badge={`${stats?.total_items ?? products.length} item`} badgeVariant="info">
      {/* KPI strip */}
      {mergeOperation && mergeOutcome && !mergeOpen && (
        <div
          role="alert"
          style={{ border: "1px solid var(--accent-red)", borderRadius: 6, padding: 10, marginBottom: 12 }}
        >
          <strong>{mergeOutcome.message}</strong>
          <p className="form-hint" style={{ margin: "4px 0" }}>
            Mã thao tác: <span style={{ fontFamily: "var(--font-mono)" }}>{mergeOperation.operationId}</span>
            {" · cặp "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{mergeOperation.canonicalId}</span>
            {" → "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{mergeOperation.aliasId}</span>
          </p>
          {mergeStatus && (
            <p className="form-hint" style={{ margin: "4px 0" }}>
              Trạng thái máy chủ: {mergeStatus.phase}
              {mergeStatus.action_required ? ` · ${mergeStatus.action_required}` : ""}
            </p>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
            <button className="btn btn-ghost" type="button" onClick={checkMergeStatus} disabled={mergeStatusLoading}>
              {mergeStatusLoading ? "Đang kiểm tra..." : "Kiểm tra trạng thái"}
            </button>
            {(mergeRestored || mergeOutcome.kind === "retryable") && (
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => {
                  // Reopen the same pair read-only; still requires preview + explicit acks.
                  setSelectedIds(new Set([mergeOperation.canonicalId, mergeOperation.aliasId]));
                  setSelectionMode(true);
                  setMergeOpen(true);
                  setMergePair([mergeOperation.canonicalId, mergeOperation.aliasId]);
                  setMergeCanonicalId(mergeOperation.canonicalId);
                  setMergePreview(null);
                  setMergeError(null);
                  setMergeAcks({ sales_stopped: false, devices_synced: false, promo: false });
                }}
              >
                Mở lại cặp sản phẩm
              </button>
            )}
          </div>
        </div>
      )}
      {stats && (
        <div className="kpi-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 12 }}>
          <KpiCard label="Tổng SP" value={stats?.total_items ?? products.length} note={`${categories.length} danh mục`} dotColor="blue" />
          <KpiCard label="Cảnh báo tồn" value={stats.low_stock} note="≤ 5" dotColor="warn" />
          <KpiCard label="Hết hàng" value={stats.out_of_stock} note="cần nhập" dotColor="bad" />
          <KpiCard label="Giá trị tồn" value={formatVND(stats.stock_retail_value)} note={`vốn ${formatVND(stats.stock_cost_value)}`} dotColor="ok" />
        </div>
      )}

      {/* Tab bar */}
      <div className="tab-bar" style={{ display: "flex", gap: 4, marginBottom: 12, borderBottom: "1px solid var(--border)" }}>
        <button
          className={`tab-btn ${activeTab === "products" ? "active" : ""}`}
          onClick={() => setActiveTab("products")}
          style={{
            padding: "8px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13,
            borderBottom: activeTab === "products" ? "2px solid var(--accent-blue)" : "2px solid transparent",
            color: activeTab === "products" ? "var(--accent-blue)" : "var(--text-secondary)",
            fontWeight: activeTab === "products" ? 600 : 400,
          }}
        >Sản phẩm ({stats?.total_items ?? products.length})</button>
        <button
          className={`tab-btn ${activeTab === "categories" ? "active" : ""}`}
          onClick={() => setActiveTab("categories")}
          style={{
            padding: "8px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13,
            borderBottom: activeTab === "categories" ? "2px solid var(--accent-blue)" : "2px solid transparent",
            color: activeTab === "categories" ? "var(--accent-blue)" : "var(--text-secondary)",
            fontWeight: activeTab === "categories" ? 600 : 400,
          }}
        >Danh mục ({categories.length})</button>
        <button
          className={`tab-btn ${activeTab === "sales" ? "active" : ""}`}
          onClick={() => setActiveTab("sales")}
          style={{
            padding: "8px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13,
            borderBottom: activeTab === "sales" ? "2px solid var(--accent-blue)" : "2px solid transparent",
            color: activeTab === "sales" ? "var(--accent-blue)" : "var(--text-secondary)",
            fontWeight: activeTab === "sales" ? 600 : 400,
          }}
        >Sale {salesStatus === "active" && sales.length > 0 ? `(${sales.length})` : ""}</button>
      </div>

      {activeTab === "sales" ? (
        <>
          <div className="filter-bar" style={{ flexWrap: "wrap", gap: 8 }}>
            <div className="seg" role="tablist" aria-label="Lọc sale">
              <button
                role="tab"
                aria-selected={salesStatus === "active"}
                className={`seg-btn ${salesStatus === "active" ? "on" : ""}`}
                onClick={() => setSalesStatus("active")}
              >
                Đang sale
              </button>
              <button
                role="tab"
                aria-selected={salesStatus === "all"}
                className={`seg-btn ${salesStatus === "all" ? "on" : ""}`}
                onClick={() => setSalesStatus("all")}
              >
                Tất cả
              </button>
            </div>
            <input
              value={salesListQ}
              onChange={(e) => setSalesListQ(e.target.value)}
              placeholder="Tìm tên / SKU / danh mục…"
              aria-label="Tìm sale"
              style={{ flex: 1, minWidth: 180, maxWidth: 360 }}
            />
            <div style={{ flex: 1 }} />
            <button
              className="btn-icon"
              onClick={async () => {
                setRefreshing(true);
                try {
                  await loadSales(true);
                  toast.success("Đã cập nhật");
                } catch { /* */ }
                setRefreshing(false);
              }}
              title="Làm mới"
              aria-label="Làm mới"
              disabled={refreshing}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: 18, animation: refreshing ? "spin 1s linear infinite" : undefined }}
              >
                refresh
              </span>
            </button>
            <button className="btn btn-secondary" onClick={handlePrintSaleLabels}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>print</span> In tem sale
            </button>
            <button className="btn" onClick={openAddSale}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span> Thêm sale
            </button>
          </div>

          {salesLoading ? (
            <SkeletonTable rows={6} cols={6} />
          ) : salesError ? (
            <ErrorState error={salesError} onRetry={() => loadSales(false)} />
          ) : sales.length === 0 ? (
            <div className="empty-state">
              <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.4 }}>
                sell
              </span>
              <p style={{ margin: 0, fontSize: 14 }}>
                {salesStatus === "active" ? "Chưa có sale đang chạy" : "Chưa có lịch sử sale"}
              </p>
              <button className="btn" style={{ marginTop: 12 }} onClick={openAddSale}>
                Thêm sale
              </button>
            </div>
          ) : (
            <DataTable
              columns={saleColumns}
              rows={saleRows}
              rowKey={(r) => String(r.id)}
              pageSize={25}
              defaultSort={{ key: "id", dir: "desc" }}
              emptyMessage={salesListQ.trim() ? "Không khớp từ khóa" : "Không có sale"}
              onVisibleRowsChange={handleSaleVisibleRowsChange}
            />
          )}

          <Modal
            open={showAddSale}
            onClose={() => setShowAddSale(false)}
            title="Thêm sale"
            width={600}
            footer={
              <>
                <button className="btn btn-ghost" onClick={() => setShowAddSale(false)}>
                  Huỷ
                </button>
                <button className="btn" onClick={handleCreateSale} disabled={saleSubmitting}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>sell</span>
                  {saleSubmitting ? "Đang tạo..." : "Tạo sale"}
                </button>
              </>
            }
          >
            <div className="form-group" style={{ position: "relative" }}>
              <label>
                Sản phẩm <span className="required">*</span>
              </label>
              <input
                value={saleProductQ}
                onChange={(e) => {
                  setSaleProductQ(e.target.value);
                  setSaleSelected(null);
                }}
                placeholder="Tên / SKU / barcode / danh mục…"
                autoFocus
                autoComplete="off"
              />
              {saleProductHits.length > 0 && !saleSelected && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 20,
                    left: 0,
                    right: 0,
                    top: "100%",
                    maxHeight: 220,
                    overflowY: "auto",
                    background: "var(--bg-card)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                  }}
                >
                  {saleProductHits.map((p) => (
                    <button
                      key={p.variant_id || p.id}
                      type="button"
                      onClick={() => pickSaleProduct(p)}
                      style={{
                        display: "flex",
                        width: "100%",
                        textAlign: "left",
                        gap: 8,
                        padding: "8px 12px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--text-primary)",
                        borderBottom: "1px solid var(--border-subtle)",
                      }}
                    >
                      <span style={{ flex: 1, fontSize: 13 }}>{p.item_name}</span>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: 0.7 }}>
                        {p.sku || "—"}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>
                        {p.default_price != null ? formatVND(p.default_price) : "—"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {saleSelected && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <strong style={{ fontSize: 13 }}>{saleSelected.item_name}</strong>
                    <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                      {formatVND(Number(saleSelected.default_price) || 0)}
                      {saleSelected.sku ? ` · SKU ${saleSelected.sku}` : ""}
                    </span>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ fontSize: 11, padding: "2px 8px", marginLeft: "auto" }}
                      onClick={() => {
                        setSaleSelected(null);
                        setSaleProductQ("");
                        setSaleProductHits([]);
                      }}
                    >
                      Đổi SP
                    </button>
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      padding: "8px 10px",
                      border: "1px solid var(--border-subtle)",
                      borderRadius: 8,
                      fontSize: 12,
                      color: "var(--text-secondary)",
                      display: "grid",
                      gap: 3,
                    }}
                  >
                    {(() => {
                      const price = Number(saleSelected.default_price) || 0;
                      const cost = Number(saleSelected.cost) || 0;
                      const margin = cost > 0 ? Math.round(((price - cost) / cost) * 100) : null;
                      return (
                        <div>
                          Giá gốc <strong style={{ color: "var(--text-primary)" }}>{formatVND(price)}</strong>
                          {" · "}vốn <strong style={{ color: "var(--text-primary)" }}>{formatVND(cost)}</strong>
                          {" · "}tồn <strong style={{ color: "var(--text-primary)" }}>{saleSelected.in_stock ?? "—"}</strong>
                          {" · "}lời {margin === null ? "—" : `${margin}%`}
                        </div>
                      );
                    })()}
                    {saleHistoryLoading && <div>Đang tải lịch sử…</div>}
                    {saleHistory && (() => {
                      const imp = saleHistory.imports[saleHistory.imports.length - 1];
                      return (
                        <>
                          {imp && (
                            <div>
                              Nhập gần nhất: {imp.order_date ? formatDate(imp.order_date) : "—"}
                              {" · "}{imp.supplier_name || "—"}
                              {" · "}{imp.quantity} đv
                              {" · "}{formatVND(imp.unit_cost)}/đv
                            </div>
                          )}
                          <div>
                            Đã bán: {saleHistory.sales.qty} · {formatVND(saleHistory.sales.revenue)}
                            {saleHistory.sales.receipt_count > 0 ? ` (${saleHistory.sales.receipt_count} hoá đơn)` : ""}
                          </div>
                          {saleHistory.reconciliation.delta !== 0 && (
                            <div style={{ color: "var(--accent-yellow)" }}>
                              Tồn lệch {saleHistory.reconciliation.delta > 0 ? "+" : ""}{saleHistory.reconciliation.delta}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={() => openHistory(saleSelected.id)}
                      style={{
                        justifySelf: "start",
                        background: "none",
                        border: "none",
                        color: "var(--accent-blue)",
                        fontSize: 12,
                        cursor: "pointer",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      Xem lịch sử đầy đủ
                    </button>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>Sale trước đây của SP này</div>
                    {saleHistoryForProduct.length === 0 ? (
                      <div style={{ color: "var(--text-secondary)" }}>Chưa từng sale</div>
                    ) : (
                      saleHistoryForProduct.slice(0, 5).map((s) => (
                        <div key={s.id} style={{ display: "flex", gap: 8, color: "var(--text-secondary)" }}>
                          <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>{formatVND(s.sale_price)}</span>
                          <span>{s.ends_at ? `hết hạn ${formatDate(s.ends_at)}` : "không hạn"}</span>
                          <span>{s.status === "active" ? "đang chạy" : "đã kết thúc"}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            {saleRelated.length > 0 && (
              <div className="form-group">
                <label>SP cùng danh mục</label>
                <div style={{ display: "grid", gap: 2, maxHeight: 168, overflowY: "auto" }}>
                  {saleRelated.map((p) => (
                    <button
                      key={p.variant_id || p.id}
                      type="button"
                      onClick={() => pickSaleProduct(p)}
                      style={{
                        display: "flex",
                        width: "100%",
                        textAlign: "left",
                        gap: 8,
                        padding: "6px 8px",
                        border: "1px solid var(--border-subtle)",
                        borderRadius: 6,
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--text-primary)",
                        fontSize: 12,
                      }}
                    >
                      <span style={{ flex: 1 }}>{p.item_name}</span>
                      <span style={{ fontWeight: 600 }}>{p.default_price != null ? formatVND(p.default_price) : "—"}</span>
                      <span style={{ opacity: 0.7 }}>tồn {p.in_stock ?? "—"}</span>
                      <span style={{ opacity: 0.7 }}>vốn {p.cost != null ? formatVND(p.cost) : "—"}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, alignItems: "start" }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>
                Giá sale <span className="required">*</span>
                {saleSelected && saleListPrice > 0 && (
                  <span style={{ marginLeft: 8, fontWeight: 400, opacity: 0.7, fontSize: 12 }}>
                    · gốc {formatVND(saleListPrice)}
                  </span>
                )}
              </label>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                {(
                  [
                    ["fixed", "Giá cố định (VND)"],
                    ["percent", "Giảm %"],
                  ] as const
                ).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    className="btn-sm"
                    onClick={() => {
                      setSalePriceMode(mode);
                      if (mode === "percent" && saleListPrice > 0) {
                        const pct = Math.min(99, Math.max(1, Math.round(Number(salePercent) || 10)));
                        setSalePercent(String(pct));
                        setSalePrice(String(salePriceFromPercent(saleListPrice, pct)));
                      }
                    }}
                    style={{
                      border: "1px solid var(--border)",
                      background: salePriceMode === mode ? "var(--accent-blue)" : "transparent",
                      color: salePriceMode === mode ? "#fff" : "var(--text-primary)",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {salePriceMode === "percent" ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="number"
                      inputMode="numeric"
                      className="num"
                      placeholder="10"
                      value={salePercent}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSalePercent(v);
                        const pct = Math.round(Number(v));
                        if (saleListPrice > 0 && pct >= 1 && pct <= 99) {
                          setSalePrice(String(salePriceFromPercent(saleListPrice, pct)));
                        }
                      }}
                      min={1}
                      max={99}
                      step={1}
                      style={{ maxWidth: 120 }}
                      aria-label="Phần trăm giảm"
                    />
                    <span style={{ fontSize: 13, opacity: 0.8 }}>%</span>
                  </div>
                  <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--text-secondary)" }}>
                    Giá sale ≈{" "}
                    <strong>
                      {saleComputedFromPct > 0 && saleComputedFromPct < saleListPrice
                        ? formatVND(saleComputedFromPct)
                        : "—"}
                    </strong>
                    {saleListPrice > 0 ? ` (gốc ${formatVND(saleListPrice)})` : " — chọn SP có giá gốc"}
                  </p>
                </>
              ) : (
                <>
                  <input
                    type="number"
                    inputMode="numeric"
                    className="num"
                    placeholder="0"
                    value={salePrice}
                    onChange={(e) => setSalePrice(e.target.value)}
                    min={0}
                    step={1000}
                    aria-label="Giá sale VND"
                  />
                  <p style={{ margin: "4px 0 0", fontSize: 11, opacity: 0.6 }}>
                    Số nguyên VND, phải thấp hơn giá gốc.
                  </p>
                </>
              )}
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Hết hạn (tuỳ chọn)</label>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                {(
                  [
                    ["today", "Hôm nay", () => { setSaleEndsAdvanced(false); setSaleEndsAt(""); setSaleEndsDate(todayDateInput()); }],
                    ["week", "Hết tuần", () => { setSaleEndsAdvanced(false); setSaleEndsAt(""); setSaleEndsDate(endOfWeekDateInput()); }],
                    ["none", "Không hạn", () => { setSaleEndsAdvanced(false); setSaleEndsAt(""); setSaleEndsDate(""); }],
                  ] as const
                ).map(([key, label, onPick]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={onPick}
                    style={{
                      border: "1px solid var(--border)",
                      background:
                        key === "none"
                          ? !saleEndsDate && !saleEndsAdvanced
                            ? "var(--accent-blue)"
                            : "transparent"
                          : !saleEndsAdvanced &&
                              saleEndsDate === (key === "today" ? todayDateInput() : endOfWeekDateInput())
                            ? "var(--accent-blue)"
                            : "transparent",
                      color:
                        (key === "none" && !saleEndsDate && !saleEndsAdvanced) ||
                        (key !== "none" &&
                          !saleEndsAdvanced &&
                          saleEndsDate === (key === "today" ? todayDateInput() : endOfWeekDateInput()))
                          ? "#fff"
                          : "var(--text-primary)",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {!saleEndsAdvanced ? (
                <>
                  <input
                    type="date"
                    value={saleEndsDate}
                    onChange={(e) => setSaleEndsDate(e.target.value)}
                    aria-label="Ngày hết hạn"
                  />
                  <p style={{ margin: "4px 0 0", fontSize: 11, opacity: 0.6 }}>
                    Hết hạn cuối ngày đã chọn (23:59:59 giờ máy).
                  </p>
                </>
              ) : (
                <input
                  type="datetime-local"
                  value={saleEndsAt}
                  onChange={(e) => setSaleEndsAt(e.target.value)}
                  aria-label="Thời điểm hết hạn"
                />
              )}
              <button
                type="button"
                onClick={() => {
                  setSaleEndsAdvanced((v) => {
                    const next = !v;
                    if (next && saleEndsDate && !saleEndsAt) {
                      setSaleEndsAt(`${saleEndsDate}T23:59`);
                    }
                    if (!next && saleEndsAt) {
                      const d = saleEndsAt.slice(0, 10);
                      if (/^\d{4}-\d{2}-\d{2}$/.test(d)) setSaleEndsDate(d);
                    }
                    return next;
                  });
                }}
                style={{
                  marginTop: 8,
                  background: "none",
                  border: "none",
                  color: "var(--accent-blue)",
                  fontSize: 12,
                  cursor: "pointer",
                  padding: 0,
                  textDecoration: "underline",
                }}
              >
                {saleEndsAdvanced ? "Dùng ngày thôi" : "Giờ cụ thể…"}
              </button>
            </div>
            </div>
            <div className="form-group">
              <label>Ghi chú</label>
              <input
                value={saleNote}
                onChange={(e) => setSaleNote(e.target.value)}
                placeholder="VD: Flash sale T7"
                maxLength={500}
              />
            </div>
          </Modal>
        </>
      ) : activeTab === "products" ? (
        <>
          {/* Toolbar */}
          <div className="filter-bar filter-bar--sticky">
            <SearchTextarea
              value={qInput}
              onChange={setQInput}
              placeholder={"1 dòng = 1 tên/SKU"}
              ariaLabel="Tìm sản phẩm theo tên hoặc SKU, nhiều dòng"
            />
            <button className="btn-icon" onClick={() => setScannerOpen(true)} title="Quét barcode" aria-label="Quét barcode">
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>qr_code_scanner</span>
            </button>
            <button
              className="btn-icon"
              onClick={async () => {
                setRefreshing(true);
                try { await loadProducts(true); toast.success("Đã cập nhật"); } catch { /* */ }
                setRefreshing(false);
              }}
              title="Làm mới"
              aria-label="Làm mới"
              disabled={refreshing}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18, animation: refreshing ? "spin 1s linear infinite" : undefined }}>refresh</span>
            </button>
            <button
              className={`btn-icon ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              title="Dạng bảng"
              aria-label="Dạng bảng"
              style={viewMode === "table" ? { background: "var(--accent-blue)", color: "#fff" } : undefined}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>view_list</span>
            </button>
            <button
              className={`btn-icon ${viewMode === "card" ? "active" : ""}`}
              onClick={() => setViewMode("card")}
              title="Dạng card"
              aria-label="Dạng card"
              style={viewMode === "card" ? { background: "var(--accent-blue)", color: "#fff" } : undefined}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>grid_view</span>
            </button>
            <button
              className={`btn-icon ${advancedFilterActive ? "active" : ""}`}
              onClick={() => setShowAdvancedFilter((v) => !v)}
              title="Bộ lọc"
              aria-label="Bộ lọc"
              style={advancedFilterActive ? { background: "var(--accent-blue)", color: "#fff" } : undefined}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>tune</span>
            </button>
          </div>

          <div className="filter-actions" style={{ display: "flex", justifyContent: "flex-end", gap: 8, margin: "8px 0" }}>
            <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={() => { setSelectionMode((v) => !v); if (selectionMode) setSelectedIds(new Set()); }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>checklist</span> {selectionMode ? "Xong chọn" : "Thao tác nhanh"}
            </button>
            <button className="btn" onClick={() => { setForm(emptyForm()); setShowCreate(true); }}><span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span> Thêm SP</button>
            <button className="btn btn-secondary" onClick={() => setShowBulkImport(true)}><span className="material-symbols-outlined" style={{ fontSize: 16 }}>upload</span> Import</button>
          </div>

          {showAdvancedFilter && (
            <div className="filter-advanced">
              {/* Segmented control: Tất cả / Workshop / Nguyên liệu (single-select) */}
              {(() => {
                const wsCat = categories.find((c) => c.name === "Workshop");
                const wsId = wsCat?.id ?? "cat_workshop";
                const matCat = categories.find((c) => c.name === "Nguyên liệu") ?? categories.find((c) => c.id === "cat_material");
                const matId = matCat?.id ?? "cat_material";
                const segs: { id: string; icon: string; label: string }[] = [
                  { id: "", icon: "apps", label: "Tất cả" },
                  { id: wsId, icon: "science", label: "Workshop" },
                  { id: matId, icon: "inventory_2", label: "Nguyên liệu" },
                ];
                return (
                  <div className="seg" role="tablist" aria-label="Lọc theo loại">
                    {segs.map((s) => (
                      <button
                        key={s.label}
                        role="tab"
                        aria-selected={categoryId === s.id}
                        className={`seg-btn ${categoryId === s.id ? "on" : ""}`}
                        onClick={() => setCategoryId(s.id)}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>{s.icon}</span>
                        {s.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
              <div className="filter-field" style={{ minWidth: 180 }}>
                <label>Danh mục ({categories.length})</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                  <option value="">Tất cả nhóm</option>
                  {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                </select>
              </div>
              <div className="filter-actions">
                {advancedFilterActive && (
                  <button className="btn btn-secondary" onClick={() => setCategoryId("")}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>filter_alt_off</span> Xoá lọc
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Product list */}
          {loading && products.length === 0 ? (
            <SkeletonTable rows={8} cols={6} />
          ) : error ? (
            <ErrorState error={error} onRetry={loadProducts} />
          ) : products.length === 0 ? (
            <div className="empty-state">
              <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.4 }}>inventory_2</span>
              <p style={{ margin: 0, fontSize: 14 }}>Không có sản phẩm phù hợp</p>
            </div>
          ) : viewMode === "table" ? (
            <DataTable
              /* key=q: remount khi query đã debounce đổi → reset sort tay + giữ thứ tự
                 relevance từ server/rankSearchResults (defaultSort đọc 1 lần lúc mount). */
              key={q}
              columns={tableColumns}
              rows={products}
              rowKey={(p) => p.id}
              pageSize={pageSize}
              defaultSort={q.trim() ? undefined : { key: "item_name", dir: "asc" }}
              onVisibleRowsChange={handleVisibleRowsChange}
            />
          ) : (
            <>
              {selectionMode && (() => {
                const togglePage = () => setSelectedIds((prev) => {
                  const next = new Set(prev);
                  const allSelected = cardPageProducts.length > 0 && cardPageProducts.every((p) => next.has(p.id));
                  for (const p of cardPageProducts) allSelected ? next.delete(p.id) : next.add(p.id);
                  return next;
                });
                return (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44, marginBottom: 6 }}>
                    <SaleCheckbox
                      label="Chọn trang này"
                      disabled={bulkSubmitting}
                      checked={cardPageProducts.length > 0 && cardPageProducts.every((p) => selectedIds.has(p.id))}
                      onToggle={togglePage}
                    />
                    <button type="button" className="btn btn-ghost" disabled={bulkSubmitting} onClick={togglePage} style={{ padding: "2px 4px", fontSize: 13 }}>Chọn trang này</button>
                  </div>
                );
              })()}
              <div className="product-grid">
                {cardPageProducts.map((p) => {
                  const cat = p.category_id ? catMap[p.category_id] : null;
                  const stock = Number(p.in_stock || 0);
                  const stockVariant: "ok" | "warn" | "bad" = stock <= 0 ? "bad" : stock <= 5 ? "warn" : "ok";
                  return (
                    <div className="product-card" key={p.id} style={{ position: "relative" }}>
                      {selectionMode && <span style={{ position: "absolute", top: 8, left: 8, zIndex: 1 }}><SaleCheckbox label={`Chọn ${p.item_name}`} disabled={bulkSubmitting} checked={selectedIds.has(p.id)} onToggle={() => toggleSelected(p.id)} /></span>}
                      <div className="img" style={{ width: 48, height: 48, flexShrink: 0 }}>
                        {p.image_url ? (
                          <img src={p.image_url} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }} />
                        ) : (
                          <ProductImageThumb productId={p.id} />
                        )}
                      </div>
                      <div className="info" style={{ flex: 1, minWidth: 0 }}>
                        <h4 style={{ margin: 0, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.item_name}</h4>
                        <div className="meta" style={{ fontSize: 11, color: "var(--text-tertiary)", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                          {cat && <span className="cat-swatch" style={{ background: colorOf(cat.color), width: 8, height: 8, borderRadius: "50%", display: "inline-block" }} />}
                          {editingCategoryId === p.id ? (
                            <select autoFocus aria-label={`Danh mục ${p.item_name}`} value={p.category_id || ""} onChange={(e) => {
                              if (e.target.value === "__create__") { openCreateCat(); setEditingCategoryId(null); return; }
                              setInlineCategory(p, e.target.value);
                            }} onBlur={() => setEditingCategoryId(null)} style={{ minHeight: 36 }}>
                              <option value="">Không phân loại</option>
                              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                              <option value="__create__">+ Tạo danh mục</option>
                            </select>
                          ) : <button className="btn btn-ghost" onClick={() => setEditingCategoryId(p.id)} title="Sửa danh mục" style={{ minHeight: 36, padding: "2px 4px", fontSize: 11 }}>{p.category_name || cat?.name || "—"}</button>}
                          {p.sku && (<><span style={{ opacity: 0.5 }}>|</span><span style={{ fontFamily: "var(--font-mono)", opacity: 0.7 }}>{p.sku}</span></>)}
                        </div>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2, fontSize: 12 }}>
                          <span style={{ fontWeight: 600 }}>{p.default_price != null ? formatVND(p.default_price) : "—"}</span>
                          {p.cost != null && <span style={{ opacity: 0.6 }}>vốn {formatVND(p.cost)}</span>}
                          <Badge variant={stockVariant} style={{ marginLeft: "auto" }}>{stock}</Badge>
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                        <button className="btn btn-ghost" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => openEdit(p.id)} title="Sửa">
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                        </button>
                        <button className="btn btn-ghost" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => openHistory(p.id)} title="Lịch sử bán / nhập">
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>history</span>
                        </button>
                        <button className="btn btn-ghost" style={{ fontSize: 11, padding: "2px 6px", color: "var(--accent-red)" }} onClick={() => handleDelete(p.id, p.item_name)} title="Xoá">
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>delete</span>
                        </button>
                        <button className="btn btn-ghost" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => handleAddToPrint([p])} title="Thêm in tem">
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>print</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {products.length > pageSize && (
                <div className="data-table-pagination">
                  <div className="page-info">Tổng {products.length} · Trang {currentPage} / {Math.ceil(products.length / pageSize)}</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>Trước</button>
                    <button disabled={currentPage >= Math.ceil(products.length / pageSize)} onClick={() => setCurrentPage((p) => p + 1)}>Sau</button>
                  </div>
                </div>
              )}
            </>
          )}

          {selectionMode && selectedIds.size > 0 && (
            <div role="toolbar" aria-label="Thao tác sản phẩm đã chọn" style={{ position: "sticky", bottom: 12, zIndex: 5, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, padding: 10, marginTop: 12, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,.16)" }}>
              <strong>Đã chọn {selectedIds.size}</strong>
              <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={() => openBulkAction("category")}>Danh mục</button>
              <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={() => openBulkAction("price")}>Giá bán</button>
              <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={() => openBulkAction("vat")}>VAT</button>
              <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={() => openBulkAction("bangke")}>Bảng kê</button>
              <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={() => openBulkAction("import_date")}>Ngày nhập</button>
              <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={exportSelectedCsv}>Xuất CSV</button>
              <button className="btn btn-secondary" disabled={bulkSubmitting} onClick={() => handleAddToPrint(selectedProducts)}>Thêm in tem</button>
              {mergeAllowed && (
                <button
                  className="btn btn-secondary"
                  disabled={bulkSubmitting || selectedIds.size !== 2}
                  onClick={openMerge}
                  title={selectedIds.size === 2 ? "Xem trước gộp 2 sản phẩm" : "Chọn đúng 2 sản phẩm"}
                >
                  Gộp sản phẩm
                </button>
              )}
              <button className="btn btn-ghost" disabled={bulkSubmitting} onClick={() => setSelectedIds(new Set())}>Bỏ chọn</button>
            </div>
          )}
        </>
      ) : (
        /* ─── Categories Tab ─── */
        <>
          <div className="filter-bar">
            <div style={{ flex: 1 }} />
            <button className="btn" onClick={openCreateCat}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span> Tạo danh mục
            </button>
          </div>
          {categories.length === 0 ? (
            <div className="empty-state">
              <span className="material-symbols-outlined" style={{ fontSize: 48, opacity: 0.4 }}>category</span>
              <p style={{ margin: 0, fontSize: 14 }}>Chưa có danh mục nào</p>
            </div>
          ) : (
            <div className="table-wrap" style={{ overflowX: "auto" }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tên</th>
                    <th>Màu</th>
                    <th style={{ textAlign: "right" }}>Số SP</th>
                    <th style={{ textAlign: "right" }}>Hành động</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          <span style={{ width: 16, height: 16, borderRadius: "50%", background: colorOf(c.color), display: "inline-block", border: "1px solid var(--border)" }} />
                          <span style={{ fontSize: 12, opacity: 0.7 }}>{c.color || "GREY"}</span>
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Badge variant={(c.item_count || 0) > 0 ? "info" : "ghost"}>{c.item_count || 0}</Badge>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                          <button className="btn btn-ghost" style={{ padding: "4px 6px" }} onClick={() => openEditCat(c)} title="Sửa">
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: "4px 6px", color: "var(--accent-red)", minWidth: 44, minHeight: 44 }}
                            onClick={() => deleteCat(c)}
                            disabled={isProtectedCat(c) || catDeleteBusy}
                            title={isProtectedCat(c) ? "Không xoá được danh mục hệ thống" : "Xoá"}
                            aria-label={isProtectedCat(c) ? `Không xoá được ${c.name}` : `Xoá ${c.name}`}
                          >
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Modal
            open={showCatModal}
            onClose={() => setShowCatModal(false)}
            title={catForm.id ? "Sửa danh mục" : "Tạo danh mục"}
            width={420}
            footer={
              <>
                <button className="btn btn-ghost" onClick={() => setShowCatModal(false)}>Huỷ</button>
                <button className="btn" onClick={saveCat}>
                  <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span> Lưu
                </button>
              </>
            }
          >
            <div className="form-group">
              <label>Tên danh mục <span className="required">*</span></label>
              <input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} autoFocus />
            </div>
            <div className="form-group">
              <label>Màu</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {COLOR_OPTIONS.map((col) => (
                  <button
                    key={col}
                    type="button"
                    onClick={() => setCatForm({ ...catForm, color: col })}
                    style={{
                      width: 40, height: 40, borderRadius: "50%",
                      background: colorOf(col), cursor: "pointer",
                      border: catForm.color === col ? "3px solid var(--text-primary)" : "1px solid var(--border)",
                      transition: "transform 0.1s",
                      transform: catForm.color === col ? "scale(1.1)" : "scale(1)",
                    }}
                    title={col}
                  />
                ))}
              </div>
            </div>
            <div className="form-group">
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="checkbox" checked={catForm.has_recipe}
                  onChange={(e) => setCatForm({ ...catForm, has_recipe: e.target.checked })} />
                Danh mục SP sản xuất (có công thức nguyên liệu)
              </label>
            </div>
          </Modal>
          <Modal
            open={catDelete !== null}
            onClose={closeCatDelete}
            closeDisabled={catDeleteBusy}
            title={catDelete ? `Xóa danh mục ${catDelete.category.name}` : "Xóa danh mục"}
            width={480}
            footer={
              <>
                <button className="btn btn-ghost" type="button" onClick={closeCatDelete} disabled={catDeleteBusy}>Huỷ</button>
                <button className="btn" type="button" onClick={confirmCatDelete} disabled={catDeleteBusy || !catDelete} style={{ background: "var(--accent-red)", borderColor: "var(--accent-red)" }}>
                  {catDeleteBusy && catDelete?.progress
                    ? `Đang chuyển ${catDelete.progress.completed}/${catDelete.progress.total}...`
                    : catDeleteBusy
                      ? "Đang xử lý..."
                      : catDelete?.failures.length
                        ? `Thử lại ${catDelete.failures.length} SP`
                        : `Chuyển ${catDelete?.total || 0} SP và xoá`}
                </button>
              </>
            }
          >
            {catDelete && (
              <>
                <p style={{ marginTop: 0 }}>
                  {catDelete.total} sản phẩm trong danh mục này. Chọn danh mục đích rồi xoá.
                </p>
                <div className="form-group">
                  <label>Sản phẩm</label>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 20, maxHeight: 180, overflowY: "auto" }}>
                    {catDelete.items.map((item) => (
                      <li key={item.id}>
                        {item.item_name}{" "}
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>({item.sku || "—"})</span>
                      </li>
                    ))}
                  </ul>
                  {catDelete.total > catDelete.items.length && (
                    <p className="form-hint" role="status">Hiển thị {catDelete.items.length} / tổng {catDelete.total}</p>
                  )}
                </div>
                {catDelete.failures.length > 0 && (
                  <div role="alert" style={{ border: "1px solid var(--accent-red)", borderRadius: 6, padding: 10, marginBottom: 12 }}>
                    <strong>Chưa chuyển được {catDelete.failures.length} SP. Danh mục chưa xoá. SP đã chuyển giữ nguyên.</strong>
                    <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
                      {catDelete.items.map((item) => {
                        const failure = catDelete.failures.find((row) => row.item_id === item.id);
                        return failure ? (
                          <li key={item.id}>
                            {item.item_name} <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>({item.sku || "—"})</span>: {failure.error}
                            {failure.reconciliation_required ? " Cần đối soát." : ""}
                          </li>
                        ) : null;
                      })}
                    </ul>
                  </div>
                )}
                {catDelete.progress && (
                  <p role="status" aria-live="polite" className="form-hint">
                    Đã xử lý {catDelete.progress.completed}/{catDelete.progress.total}
                  </p>
                )}
                <div className="form-group">
                  <label htmlFor="cat-delete-target">Chuyển sang</label>
                  <select
                    id="cat-delete-target"
                    value={catDelete.targetId}
                    disabled={catDeleteBusy}
                    onChange={(e) => setCatDelete((prev) => prev && { ...prev, targetId: e.target.value })}
                    style={{ minHeight: 44 }}
                  >
                    <option value="">Không phân loại</option>
                    {categories.filter((c) => c.id !== catDelete.category.id).map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </Modal>
        </>
      )}

      <Modal
        open={bulkAction !== null}
        onClose={closeBulkAction}
        title={bulkAction === "category" ? "Đổi danh mục" : bulkAction === "price" ? "Đổi giá bán" : bulkAction === "sale" ? "Cập nhật sale" : bulkAction === "vat" ? "Cập nhật VAT" : bulkAction === "bangke" ? "Cập nhật bảng kê" : "Cập nhật ngày nhập"}
        width={440}
        footer={<><button className="btn btn-ghost" type="button" onClick={closeBulkAction} disabled={bulkSubmitting}>Huỷ</button><button className="btn" type="button" onClick={submitBulkAction} disabled={bulkSubmitting}>{bulkSubmitting && bulkAction === "category" && bulkCategoryProgress ? `Đang cập nhật ${bulkCategoryProgress.completed}/${bulkCategoryProgress.total}...` : bulkSubmitting ? "Đang cập nhật..." : `Cập nhật ${bulkAction === "category" ? bulkCategoryTargets.length : selectedIds.size} SP`}</button></>}
      >
        <p style={{ marginTop: 0 }}>Áp dụng cho <strong>{bulkAction === "category" ? bulkCategoryTargets.length : selectedIds.size}</strong> sản phẩm.</p>
        {bulkAction === "category" && bulkCategoryProgress && <p role="status" aria-live="polite" className="form-hint">Đã xử lý {bulkCategoryProgress.completed}/{bulkCategoryProgress.total}</p>}
        {bulkAction === "category" && <>
          <div className="form-group">
            <label>Mục tiêu đã chốt</label>
            <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
              {bulkCategoryTargets.slice(0, 3).map((target) => <li key={target.item_id}>{target.item_name} <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>({target.sku})</span></li>)}
            </ul>
            {bulkCategoryTargets.length > 3 && <p className="form-hint">+{bulkCategoryTargets.length - 3} SP khác</p>}
          </div>
          {bulkCategoryFailures.length > 0 && <div role="alert" style={{ border: "1px solid var(--accent-red)", borderRadius: 6, padding: 10, marginBottom: 12 }}>
            <strong>Chưa cập nhật được {bulkCategoryFailures.length} SP. Giữ lại để thử lại.</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
              {bulkCategoryTargets.map((target) => {
                const failure = bulkCategoryFailures.find((row) => row.item_id === target.item_id);
                return failure ? <li key={target.item_id}>{target.item_name} <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>({target.sku})</span>: {failure.error}{failure.reconciliation_required ? " Cần đối soát." : ""}</li> : null;
              })}
            </ul>
          </div>}
          <div className="form-group"><label>Danh mục</label><select value={bulkValue} disabled={bulkSubmitting} onChange={(e) => { bulkCategoryValueRef.current = e.target.value; setBulkValue(e.target.value); }}><option value="">Không phân loại</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
        </>}
        {bulkAction === "price" && <><div className="form-group"><label>Kiểu thay đổi</label><select value={bulkMode} onChange={(e) => setBulkMode(e.target.value as "fixed" | "percent")}><option value="fixed">Giá cố định VND</option><option value="percent">Tăng/giảm %</option></select></div><div className="form-group"><label>{bulkMode === "fixed" ? "Giá bán mới (VND)" : "Phần trăm, âm = giảm"}</label><input autoFocus type="number" inputMode="numeric" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} /></div>{bulkValue && <p className="form-hint">Ví dụ xem trước: {selectedProducts.slice(0, 3).map((p) => `${formatVND(p.default_price || 0)} → ${formatVND(bulkMode === "fixed" ? Number(bulkValue) : Math.round((p.default_price || 0) * (1 + Number(bulkValue) / 100)))}`).join(" · ")}</p>}</>}
        {(bulkAction === "vat" || bulkAction === "bangke") && <div className="form-group"><label>Thiết lập</label><select value={String(bulkEnabled)} onChange={(e) => setBulkEnabled(e.target.value === "true")}><option value="true">Bật</option><option value="false">Tắt</option></select></div>}
        {bulkAction === "import_date" && <div className="form-group"><label>Ngày nhập</label><input type="date" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} /><button className="btn btn-ghost" type="button" onClick={() => setBulkValue("")}>Xoá ngày</button></div>}
        {bulkAction === "sale" && <><div className="form-group"><label>Thao tác</label><select value={bulkSaleAction} onChange={(e) => setBulkSaleAction(e.target.value as "create" | "set_expiry" | "end")}><option value="create">Tạo sale</option><option value="set_expiry">Đổi hạn sale</option><option value="end">Kết thúc sale</option></select></div>{bulkSaleAction === "create" && <><div className="form-group"><label>Kiểu sale</label><select value={bulkMode} onChange={(e) => setBulkMode(e.target.value as "fixed" | "percent")}><option value="fixed">Giá cố định VND</option><option value="percent">Giảm %</option></select></div><div className="form-group"><label>{bulkMode === "fixed" ? "Giá sale (VND)" : "Giảm (%)"}</label><input type="number" inputMode="numeric" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} /></div></>} {bulkSaleAction !== "end" && <div className="form-group"><label>Hết hạn</label><input type="date" value={bulkEndsDate} onChange={(e) => setBulkEndsDate(e.target.value)} /><div style={{ display: "flex", gap: 6, marginTop: 6 }}><button className="btn btn-ghost" type="button" onClick={() => setBulkEndsDate(todayDateInput())}>Hôm nay</button><button className="btn btn-ghost" type="button" onClick={() => setBulkEndsDate(endOfWeekDateInput())}>Hết tuần</button><button className="btn btn-ghost" type="button" onClick={() => setBulkEndsDate("")}>Không hạn</button></div></div>}</>}
      </Modal>

      {/* Modal: Gộp sản phẩm — preview + guarded commit */}
      <Modal
        open={mergeOpen && !!mergePair}
        onClose={closeMerge}
        title="Gộp sản phẩm"
        width={880}
        pinFooter
        closeDisabled={mergeSubmitting}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={closeMerge} disabled={mergeSubmitting}>Đóng</button>
            {mergeOperation && (mergeOutcome?.kind === "unknown" || mergeOutcome?.kind === "needs_reconciliation" || mergeOutcome?.kind === "pending" || mergeOutcome?.kind === "disabled" || mergeStatus) && (
              <button className="btn btn-ghost" type="button" onClick={checkMergeStatus} disabled={mergeStatusLoading}>
                {mergeStatusLoading ? "Đang kiểm tra..." : "Kiểm tra trạng thái"}
              </button>
            )}
            <button
              className="btn"
              type="button"
              onClick={submitMerge}
              disabled={!!mergeSubmitDisabledReason || mergeSubmitting || mergeLoading}
              title={mergeSubmitDisabledReason || MERGE_OFFLINE_RISK}
            >
              {mergeSubmitting ? "Đang gộp..." : "Xác nhận gộp"}
            </button>
          </>
        }
      >
        {mergePair && (
          <>
            <p style={{ marginTop: 0 }} role="status" aria-live="polite">
              {mergeOutcome?.kind === "active"
                ? "Đã gộp sản phẩm. Lịch sử giữ nguyên; tồn kho chuyển về sản phẩm chính."
                : mergeSubmitting
                  ? "Đang thực hiện gộp. Không gửi lại thao tác."
                  : mergeOutcome || mergeOperation
                    ? "Thao tác đã được gửi. Xem kết quả máy chủ bên dưới trước khi làm tiếp."
                    : <><Badge variant="warn">Chưa thay đổi dữ liệu</Badge>{" "}<span className="form-hint">Bản xem trước chỉ đọc. Chưa có thao tác gộp nào được chạy cho tới khi bạn xác nhận.</span></>}
            </p>
            <p className="form-hint" style={{ marginTop: 0 }}>
              Chọn sản phẩm chính (canonical). Sản phẩm còn lại sẽ là tên phụ (alias). Dữ liệu lịch sử của cả hai
              vẫn giữ nguyên danh tính gốc.
            </p>
            {!mergeAllowed && (
              <p
                className="form-hint"
                role="status"
                aria-live="polite"
                style={{ marginTop: 0, padding: "8px 12px", borderRadius: 8, background: "var(--status-amber-bg)", border: "1px solid var(--status-amber-border)", color: "var(--status-amber)" }}
              >
                {mergeCapabilityDenied
                  ? "Tài khoản hiện tại không có quyền Gộp sản phẩm. Cần quản trị viên cấp quyền can_merge_products — không cần nhập mật khẩu hay mã bí mật."
                  : "Chưa xác minh được quyền Gộp sản phẩm (đang tải hoặc không đọc được /auth/me). Nút Xác nhận gộp tạm khoá."}
              </p>
            )}

            <fieldset disabled={mergeSubmitting || mergeOutcome?.kind === "active" || !!mergeOperation} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: 12, marginBottom: 12 }}>
              <legend style={{ padding: "0 6px", fontSize: 12, color: "var(--text-secondary)" }}>Sản phẩm chính</legend>
              {mergePair.map((id) => {
                const p = products.find((x) => x.id === id);
                return (
                  <label key={id} style={{ display: "flex", alignItems: "flex-start", gap: 10, minHeight: 44, padding: "6px 0", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="merge-canonical"
                      value={id}
                      checked={mergeCanonicalId === id}
                      onChange={() => setMergeCanonicalId(id)}
                      autoFocus={mergeCanonicalId === id}
                      style={{ marginTop: 4, width: 18, height: 18 }}
                    />
                    <span>
                      <strong>{p?.item_name || id}</strong>
                      <span className="form-hint" style={{ display: "block" }}>
                        SKU: {p?.sku || "—"} · Barcode: {p?.barcode || "—"} · Giá: {p?.default_price != null ? formatVND(p.default_price) : "—"} · Tồn: {p?.in_stock ?? "—"}
                      </span>
                      {mergeCanonicalId !== id && <span className="form-hint" style={{ display: "block" }}>Sẽ là sản phẩm phụ (alias)</span>}
                    </span>
                  </label>
                );
              })}
            </fieldset>

            {!mergeCanonicalId && <p className="form-hint" role="status">Chọn sản phẩm chính để tải bản xem trước.</p>}
            {(mergeRestored || mergePairConflict) && (
              <div role="alert" style={{ border: "1px solid var(--accent-red)", borderRadius: 6, padding: 10, marginBottom: 12 }}>
                {mergeRestored
                  ? "Có thao tác gộp chưa xác định từ phiên trước. Kiểm tra trạng thái trước khi xác nhận gộp."
                  : mergePairConflict}
              </div>
            )}
            {mergeLoading && <p className="form-hint" role="status" aria-live="polite">Đang tải bản xem trước…</p>}
            {mergeError && (
              <div role="alert" style={{ border: "1px solid var(--accent-red)", borderRadius: 6, padding: 10, marginBottom: 12 }}>
                {mergeError}
              </div>
            )}
            {!mergeLoading && !mergeError && mergePreview && (() => {
              const pv = mergePreview;
              const memberRows = pv.history.by_member;
              return (
                <>
                  {!pv.compatible && (
                    <div role="alert" style={{ border: "1px solid var(--accent-red)", borderRadius: 6, padding: 10, marginBottom: 12 }}>
                      <strong>Không thể gộp:</strong>
                      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                        {pv.blockers.map((b, i) => <li key={`${b.code}-${i}`}>{b.message} <span className="form-hint">({b.code})</span></li>)}
                      </ul>
                    </div>
                  )}
                  {(() => {
                    // Older worker warning claims the feature is "not implemented"; it is
                    // deployed but execution-gated. The accurate reason is shown above.
                    const pvWarnings = pv.warnings.filter((w) => !w.includes("(execution_available=false)"));
                    return pvWarnings.length > 0 && (
                      <div style={{ border: "1px solid var(--status-amber-border, var(--border))", borderRadius: 6, padding: 10, marginBottom: 12 }}>
                        <strong>Cảnh báo</strong>
                        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                          {pvWarnings.map((w, i) => <li key={i}>{w}</li>)}
                        </ul>
                      </div>
                    );
                  })()}

                  <div className="form-section-title">Lịch sử bán &amp; nhập (snapshot lịch sử)</div>
                  <div className="table-wrap" style={{ maxHeight: "none" }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Sản phẩm</th>
                          <th style={{ textAlign: "right" }}>Dòng bán</th>
                          <th style={{ textAlign: "right" }}>Doanh thu</th>
                          <th style={{ textAlign: "right" }}>Giá vốn</th>
                          <th style={{ textAlign: "right" }}>Lợi nhuận (DT − GV)</th>
                          <th style={{ textAlign: "right" }}>Dòng nhập</th>
                          <th style={{ textAlign: "right" }}>Lô nhập</th>
                        </tr>
                      </thead>
                      <tbody>
                        {memberRows.map((m) => (
                          <tr key={m.item_id}>
                            <td>
                              {pv.canonical.item_id === m.item_id ? pv.canonical.name : pv.aliases.find((a) => a.item_id === m.item_id)?.name || m.item_id}
                              <span className="form-hint"> · {m.role === "canonical" ? "chính" : "phụ"}</span>
                            </td>
                            <td style={{ textAlign: "right" }}>{m.sales.line_count}</td>
                            <td style={{ textAlign: "right" }}>{formatVND(m.sales.revenue)}</td>
                            <td style={{ textAlign: "right" }}>{formatVND(m.sales.cost)}</td>
                            <td style={{ textAlign: "right" }}>{formatVND(m.sales.revenue - m.sales.cost)}</td>
                            <td style={{ textAlign: "right" }}>{m.purchases.line_count}</td>
                            <td style={{ textAlign: "right" }}>{m.purchases.lot_count}</td>
                          </tr>
                        ))}
                        <tr>
                          <td><strong>Tổng cộng</strong></td>
                          <td style={{ textAlign: "right" }}><strong>{pv.history.sales.line_count}</strong></td>
                          <td style={{ textAlign: "right" }}><strong>{formatVND(pv.history.sales.revenue)}</strong></td>
                          <td style={{ textAlign: "right" }}><strong>{formatVND(pv.history.sales.cost)}</strong></td>
                          <td style={{ textAlign: "right" }}><strong>{formatVND(pv.history.sales.revenue - pv.history.sales.cost)}</strong></td>
                          <td style={{ textAlign: "right" }}><strong>{pv.history.purchases.line_count}</strong></td>
                          <td style={{ textAlign: "right" }}><strong>{pv.history.purchases.lot_count}</strong></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="form-hint">
                    Lợi nhuận = doanh thu − giá vốn đã ghi nhận trên từng dòng lịch sử (không dùng giá vốn hiện tại).
                    “Dòng bán” là số dòng, không phải số lượng. “Dòng nhập”/“Lô nhập” là số đếm, không phải số lượng.
                  </p>
                  <p className="form-hint">
                    Nguồn bán — hoá đơn: {formatVND(pv.history.sources.receipts.revenue)} ({pv.history.sources.receipts.line_count} dòng) ·
                    POS: {formatVND(pv.history.sources.counter.revenue)} ({pv.history.sources.counter.line_count} dòng) ·
                    Sheet cũ: {formatVND(pv.history.sources.legacy_sheet.revenue)} ({pv.history.sources.legacy_sheet.line_count} dòng, đã quy được)
                  </p>

                  <div className="form-section-title">Khuyến mãi đang hiệu lực</div>
                  {pv.canonical.active_sales.length === 0 && pv.aliases.every((a) => a.active_sales.length === 0) ? (
                    <p className="form-hint">Không có khuyến mãi hiệu lực.</p>
                  ) : (
                    <ul style={{ marginTop: 0 }}>
                      {[pv.canonical, ...pv.aliases].flatMap((it) =>
                        it.active_sales.map((s) => (
                          <li key={`${it.item_id}-${s.id}`}>
                            {it.name}{" "}
                            <span className="form-hint">
                              · {formatVND(s.sale_price)} · {formatDate(s.starts_at)} → {s.ends_at ? formatDate(s.ends_at) : "không hạn"}
                            </span>
                          </li>
                        )),
                      )}
                    </ul>
                  )}

                  <div className="form-section-title">Tồn kho theo cửa hàng</div>
                  <div className="table-wrap" style={{ maxHeight: "none" }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Cửa hàng</th>
                          <th style={{ textAlign: "right" }}>Chính</th>
                          <th style={{ textAlign: "right" }}>Phụ</th>
                          <th style={{ textAlign: "right" }}>Cộng</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pv.stock.by_store.length === 0 ? (
                          <tr><td colSpan={4} className="form-hint">Không có tồn kho theo cửa hàng.</td></tr>
                        ) : pv.stock.by_store.map((s) => (
                          <tr key={s.store_id}>
                            <td style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{s.store_id}</td>
                            <td style={{ textAlign: "right" }}>{s.canonical_quantity}</td>
                            <td style={{ textAlign: "right" }}>{s.alias_quantity}</td>
                            <td style={{ textAlign: "right" }}>{s.combined_quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="form-hint">{pv.stock.label}. Đây là số sổ, không phải số kiểm đếm thực tế.</p>

                  {pv.history.reconciliation && pv.history.reconciliation.ambiguous_refund_lines > 0 && (
                    <div role="alert" style={{ border: "1px solid var(--accent-red)", borderRadius: 6, padding: 10, marginBottom: 12 }}>
                      <strong>Lịch sử hoàn hàng chưa chắc chắn:</strong>{" "}
                      {pv.history.reconciliation.ambiguous_refund_lines} dòng hoàn hàng POS không có mã hoá đơn hoàn trả riêng.
                      Các khoản này <strong>không</strong> được trừ vào tổng doanh thu ở trên; cần đối chiếu tay.
                    </div>
                  )}

                  {!pv.execution_available && (
                    <p
                      role="status"
                      aria-live="polite"
                      style={{ padding: "8px 12px", borderRadius: 8, marginBottom: 12, background: "var(--status-amber-bg)", border: "1px solid var(--status-amber-border)", color: "var(--status-amber)" }}
                    >
                      {mergeExecutionUnavailableReason(pv.capability)}
                      {" "}Nút Xác nhận gộp tạm khoá; bạn vẫn xem được bản xem trước.
                    </p>
                  )}

                  {mergePreview.execution_available && (
                    <div style={{ borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 12 }}>
                      <div className="form-section-title">Xác nhận trước khi gộp</div>
                      <div role="note" style={{ border: "1px solid var(--status-amber-border, var(--border))", borderRadius: 6, padding: 10, marginBottom: 12 }}>
                        <strong>Rủi ro offline:</strong> {MERGE_OFFLINE_RISK}
                      </div>
                      <label style={{ display: "flex", alignItems: "flex-start", gap: 10, minHeight: 44, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={mergeAcks.sales_stopped}
                          disabled={mergeSubmitting}
                          onChange={(e) => setMergeAcks((prev) => ({ ...prev, sales_stopped: e.target.checked }))}
                          style={{ marginTop: 3, width: 18, height: 18 }}
                        />
                        <span>Tôi đã tạm dừng bán cả hai sản phẩm trên mọi thiết bị.</span>
                      </label>
                      <label style={{ display: "flex", alignItems: "flex-start", gap: 10, minHeight: 44, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={mergeAcks.devices_synced}
                          disabled={mergeSubmitting}
                          onChange={(e) => setMergeAcks((prev) => ({ ...prev, devices_synced: e.target.checked }))}
                          style={{ marginTop: 3, width: 18, height: 18 }}
                        />
                        <span>Tôi đã đồng bộ mọi thiết bị POS (không còn hàng đợi offline).</span>
                      </label>
                      {mergeCanonicalPromo && (
                        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, minHeight: 44, cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={mergeAcks.promo}
                            disabled={mergeSubmitting}
                            onChange={(e) => setMergeAcks((prev) => ({ ...prev, promo: e.target.checked }))}
                            style={{ marginTop: 3, width: 18, height: 18 }}
                          />
                          <span>Tôi xác nhận khuyến mãi đang chạy của sản phẩm chính được giữ nguyên.</span>
                        </label>
                      )}
                      {mergePairConflict && <p className="form-hint" role="alert" style={{ color: "var(--accent-red)" }}>{mergePairConflict}</p>}
                      {mergeBlockedReason && <p className="form-hint" role="status">{mergeBlockedReason}</p>}
                    </div>
                  )}

                  {mergeOutcome && (
                    <div
                      role={mergeOutcome.kind === "needs_reconciliation" || mergeOutcome.kind === "unknown" ? "alert" : "status"}
                      style={{
                        border: `1px solid ${mergeOutcome.kind === "active" ? "var(--accent)" : mergeOutcome.kind === "needs_reconciliation" || mergeOutcome.kind === "unknown" ? "var(--accent-red)" : "var(--border)"}`,
                        borderRadius: 6,
                        padding: 10,
                        marginTop: 12,
                      }}
                    >
                      <strong>{mergeOutcome.message}</strong>
                      {mergeOperation && (
                        <p className="form-hint" style={{ marginBottom: 0 }}>
                          Mã thao tác: <span style={{ fontFamily: "var(--font-mono)" }}>{mergeOperation.operationId}</span>
                          {mergeOutcome.keepOperationId ? " — giữ nguyên mã này khi kiểm tra lại." : ""}
                        </p>
                      )}
                      {mergeStatus && (
                        <p className="form-hint" style={{ marginBottom: 0 }}>
                          Trạng thái máy chủ: {mergeStatus.phase}
                          {mergeStatus.locks.length > 0 ? ` · đang giữ ${mergeStatus.locks.length} khoá` : ""}
                          {mergeStatus.action_required ? ` · ${mergeStatus.action_required}` : ""}
                        </p>
                      )}
                      {mergeOutcome.kind === "stale_preview" && (
                        <button
                          className="btn btn-ghost"
                          type="button"
                          style={{ marginTop: 8 }}
                          onClick={() => setMergeReloadKey((k) => k + 1)}
                        >
                          Tải lại bản xem trước
                        </button>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </>
        )}
      </Modal>


      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Thêm sản phẩm mới"
        width={760}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Huỷ</button>
            <button className="btn" onClick={handleCreate} disabled={submitting}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
              {submitting ? "Đang tạo..." : "Lưu sản phẩm"}
            </button>
          </>
        }
      >
        {renderFormBody()}
      </Modal>

      {/* Modal: Sửa SP */}
      <Modal
        open={showEdit && !!editDetail}
        onClose={() => { setShowEdit(false); setEditDetail(null); }}
        title="Sửa sản phẩm"
        width={760}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => { setShowEdit(false); setEditDetail(null); }}>Huỷ</button>
            <button className="btn" onClick={handleEdit} disabled={submitting}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>save</span>
              {submitting ? "Đang lưu..." : "Cập nhật"}
            </button>
          </>
        }
      >
        {renderFormBody()}
        {editDetail && editDetail.audit_log.length > 0 && (
          <details style={{ marginTop: 16, fontSize: 12, paddingTop: 12, borderTop: "1px solid var(--border-subtle)" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Lịch sử thay đổi ({editDetail.audit_log.length})
            </summary>
            <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 8 }}>
              {editDetail.audit_log.map((log) => (
                <div key={log.id} style={{ padding: "4px 0", borderBottom: "1px solid var(--border-subtle)", display: "flex", gap: 8 }}>
                  <span style={{ opacity: 0.6 }}>{formatDate(log.changed_at)}</span>
                  <span>{log.action}</span>
                  {log.field_changed && <span style={{ color: "var(--accent)" }}>{log.field_changed}: {log.old_value} → {log.new_value}</span>}
                  <span style={{ opacity: 0.5 }}>{log.changed_by}</span>
                </div>
              ))}
            </div>
          </details>
        )}
      </Modal>

      {/* Modal: Lịch sử bán / nhập / audit */}
      <Modal
        open={!!showHistory}
        onClose={() => { setShowHistory(null); setHistoryMeta(null); }}
        title={`Lịch sử · ${historyMeta?.name ? (historyMeta.name.length > 36 ? historyMeta.name.slice(0, 36) + "…" : historyMeta.name) : ""}`}
        width={640}
      >
        <div style={{ display: "flex", gap: 4, marginBottom: 12, flexWrap: "wrap" }}>
          {([
            ["overview", "Tổng quan", 0],
            ["sales", "Đơn bán", historySales.length],
            ["lots", "Lô nhập", historyLots.length],
            ["audit", "Thay đổi", historyAudit.length],
          ] as const).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              className={`btn btn-ghost${historyTab === key ? " active" : ""}`}
              style={{
                fontSize: 12,
                padding: "4px 10px",
                background: historyTab === key ? "var(--bg-tertiary)" : undefined,
                fontWeight: historyTab === key ? 600 : 400,
              }}
              onClick={() => setHistoryTab(key)}
            >
              {label}{count > 0 ? ` (${count})` : ""}
            </button>
          ))}
        </div>
        <div style={{ maxHeight: "60vh", overflowY: "auto", fontSize: 12 }}>
          {historyLoading ? (
            <div style={{ padding: 24, textAlign: "center", opacity: 0.6 }}>Đang tải…</div>
          ) : historyTab === "overview" ? (
            historyOverviewLoading ? (
              <div style={{ padding: 24, textAlign: "center", opacity: 0.6 }}>Đang tải…</div>
            ) : !historyOverview ? (
              <div style={{ padding: 16, textAlign: "center", opacity: 0.6 }}>Không tải được dữ liệu tổng quan</div>
            ) : (
              (() => {
                const h = historyOverview;
                const goodsType = (t: string | null) =>
                  t === "bangke" ? "Bảng kê" : t === "vat" ? "VAT" : t === "tieungach" ? "Tiểu ngạch" : (t || "—");
                const initialQty = h.initial_lots.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
                const initialDate = h.initial_lots[0]?.created_at || null;
                const r = h.reconciliation;
                return (
                  <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Sản phẩm</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
                        <span>Giá bán: <b>{formatVND(h.product.default_price)}</b></span>
                        <span>Vốn: <b>{formatVND(h.product.cost)}</b></span>
                        <span>Tồn hiện tại: <b>{h.product.in_stock}</b></span>
                        <span>SKU: {h.product.sku || "—"}</span>
                        <span>Barcode: {h.product.barcode || "—"}</span>
                        <span>Ngày tạo: {h.product.created_at ? formatDate(h.product.created_at) : "—"}</span>
                        <span>Danh mục: {h.product.category_name || "—"}</span>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Nhập hàng</div>
                      {h.initial_lots.length > 0 && (
                        <div style={{ marginBottom: 6, opacity: 0.8 }}>
                          Tồn gốc lúc tạo SP: <b>{initialQty} con</b>{initialDate ? ` (${formatDate(initialDate)})` : ""}
                        </div>
                      )}
                      {h.imports.length === 0 ? (
                        <div style={{ opacity: 0.6 }}>Chưa có đơn nhập</div>
                      ) : (
                        <div className="table-wrap">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Ngày</th>
                                <th>NCC</th>
                                <th>Loại hàng</th>
                                <th style={{ textAlign: "right" }}>SL</th>
                                <th style={{ textAlign: "right" }}>Vốn/đv</th>
                              </tr>
                            </thead>
                            <tbody>
                              {h.imports.map((im) => (
                                <tr key={im.import_order_id}>
                                  <td>{im.order_date ? formatDate(im.order_date) : "—"}</td>
                                  <td>{im.supplier_name || "—"}</td>
                                  <td>{goodsType(im.goods_type)}</td>
                                  <td style={{ textAlign: "right" }}>{im.quantity}</td>
                                  <td style={{ textAlign: "right" }}>{formatVND(im.unit_cost)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Bán hàng</div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
                        <span>Tổng SL đã bán: <b>{h.sales.qty}</b></span>
                        <span>Doanh thu: <b>{formatVND(h.sales.revenue)}</b></span>
                        <span>Số hóa đơn: <b>{h.sales.receipt_count}</b></span>
                        <span>Đơn đầu: {h.sales.first_at ? formatDate(h.sales.first_at) : "—"}</span>
                        <span>Đơn cuối: {h.sales.last_at ? formatDate(h.sales.last_at) : "—"}</span>
                      </div>
                      {h.sheet_sales && (
                        <div className="form-hint" style={{ marginTop: 6 }}>
                          Lịch sử cũ (trước POS): {h.sheet_sales.qty} con · {formatVND(h.sheet_sales.revenue)} (từ {formatDate(h.sheet_sales.first)} đến {formatDate(h.sheet_sales.last)})
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>Đối soát tồn</div>
                      <div style={{ opacity: 0.85 }}>
                        Tồn gốc ({r.initial}) + Nhập ({r.imported}) − Bán ({r.sold}) ± Chỉnh tay ({r.adjusted >= 0 ? `+${r.adjusted}` : r.adjusted}) = Dự kiến (<b>{r.expected}</b>)
                      </div>
                      <div style={{ marginTop: 4 }}>Thực tế: <b>{r.actual}</b></div>
                      {r.delta === 0 ? (
                        <div style={{ marginTop: 6, color: "var(--accent-green, #34d399)", fontWeight: 600 }}>✓ Khớp</div>
                      ) : (
                        <div style={{ marginTop: 6, padding: "8px 10px", borderRadius: 8, background: "rgba(251, 191, 36, 0.12)", border: "1px solid rgba(251, 191, 36, 0.4)" }}>
                          <div style={{ fontWeight: 600, color: "#fbbf24" }}>⚠ Lệch {r.delta} con</div>
                          {h.adjustments.length > 0 && (
                            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                              {h.adjustments.map((a, i) => (
                                <div key={i} style={{ opacity: 0.85 }}>
                                  {formatDate(a.changed_at)} · {a.action}: {a.old_value ?? "—"} → {a.new_value ?? "—"} ({a.delta >= 0 ? `+${a.delta}` : a.delta}){a.changed_by ? ` · ${a.changed_by}` : ""}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {h.material_note && (
                        <div className="form-hint" style={{ marginTop: 6 }}>
                          Nguyên liệu tiêu hao theo công thức nên đối soát có thể lệch.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()
            )
          ) : historyTab === "sales" ? (
            historySales.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", opacity: 0.75 }}>
                <div style={{ marginBottom: 8 }}>Chưa có đơn bán trong 12 tháng</div>
                <Link
                  to={`/sales-history?q=${encodeURIComponent(historyMeta?.sku || historyMeta?.name || "")}`}
                  style={{ color: "var(--accent-blue)" }}
                >
                  Xem full lịch sử bán
                </Link>
              </div>
            ) : (
              <>
                {historySales.map((r) => (
                  <div key={`${r.id}-${r.receipt_number || ""}-${r.sale_date}`} style={{ padding: "6px 0", borderBottom: "1px solid var(--border-subtle)", display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 8, alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{formatDate(r.sold_at || r.sale_date)}</div>
                      <div style={{ opacity: 0.65 }}>
                        {r.receipt_number ? (
                          <Link to={`/orders/${r.receipt_number}`} style={{ color: "var(--accent-blue)" }}>#{r.receipt_number}</Link>
                        ) : (
                          <span>—</span>
                        )}
                        {r.source ? <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 4, background: "var(--bg-tertiary)", fontSize: 10 }}>{r.source}</span> : null}
                      </div>
                    </div>
                    <span style={{ textAlign: "right" }}>×{r.qty}</span>
                    <span style={{ textAlign: "right", opacity: 0.75 }}>{formatVND(r.unit_price)}</span>
                    <span style={{ textAlign: "right", fontWeight: 600 }}>{formatVND(r.revenue)}</span>
                  </div>
                ))}
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", fontWeight: 600 }}>
                  <span>Tổng ×{historySales.reduce((s, r) => s + (Number(r.qty) || 0), 0)}</span>
                  <span>{formatVND(historySales.reduce((s, r) => s + (Number(r.revenue) || 0), 0))}</span>
                </div>
              </>
            )
          ) : historyTab === "lots" ? (
            historyLots.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", opacity: 0.6 }}>Chưa có lô nhập</div>
            ) : (
              historyLots.map((lot: any) => (
                <div key={lot.id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 500 }}>{formatDate(lot.order_date || lot.created_at)}</span>
                    <span>
                      {lot.import_order_id != null ? `#${lot.import_order_id}` : (lot.source || "—")}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: 12, opacity: 0.8, marginTop: 2, flexWrap: "wrap" }}>
                    <span>×{lot.quantity ?? lot.qty ?? "—"}</span>
                    {lot.unit_cost != null && <span>vốn {formatVND(lot.unit_cost)}</span>}
                    {lot.remaining != null && <span>còn {lot.remaining}</span>}
                    {lot.supplier_name && <span>{lot.supplier_name}</span>}
                  </div>
                </div>
              ))
            )
          ) : historyAudit.length === 0 ? (
            <div style={{ padding: 16, textAlign: "center", opacity: 0.6 }}>Chưa có lịch sử thay đổi</div>
          ) : (
            historyAudit.map((log: any) => (
              <div key={log.id} style={{ padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontWeight: 500 }}>{log.action}</span>
                  <span style={{ opacity: 0.6 }}>{formatDate(log.changed_at)}</span>
                </div>
                {log.field_changed && (
                  <div style={{ color: "var(--text-secondary)" }}>
                    {log.field_changed}: {log.old_value} → {log.new_value}
                  </div>
                )}
                <div style={{ opacity: 0.5 }}>{log.changed_by} ({log.source})</div>
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* Modal: Bulk Import */}
      <Modal
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        title="Import sản phẩm (Markdown)"
        width={680}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setShowBulkImport(false)}>Huỷ</button>
            <button className="btn" onClick={handleBulkImport} disabled={importing}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>upload</span>
              {importing ? "Đang import..." : "Import"}
            </button>
          </>
        }
      >
        <div className="form-group">
          <label>Dán bảng markdown — mỗi dòng là 1 SP</label>
          <div className="form-hint">Định dạng: <code>| Tên | SKU | Giá bán | Giá vốn |</code></div>
          <textarea
            placeholder={"| Tên SP | SKU | Giá bán | Giá vốn |\n| Bút bi | PEN01 | 5000 | 3000 |"}
            value={bulkMd}
            onChange={(e) => setBulkMd(e.target.value)}
            rows={10}
            style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}
          />
        </div>
      </Modal>
      {scannerOpen && <BarcodeScanner onScan={(code) => { setQInput(code); setScannerOpen(false); }} onClose={() => setScannerOpen(false)} />}

      {/* ── Recipe Drawer (W7) ────────────────────── */}
      {/* createPortal: render ra document.body để tránh ancestor .page-wrap.fade-up (transform) thành containing block → position:fixed bị lệch viewport */}
      {recipeProduct && createPortal(
        <>
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={closeRecipe}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%", maxWidth: 480, maxHeight: "92vh",
                background: "var(--bg-surface, #0a0a0c)",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 14,
                boxShadow: "0 24px 64px rgba(0,0,0,0.7)",
                display: "flex", flexDirection: "column",
                overflow: "hidden",
                animation: "popIn 0.2s ease-out",
              }}
            >
              {/* Header */}
              <div style={{ padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.10)", display: "flex", alignItems: "center", gap: 10, background: "var(--bg-card)" }}>
                <span style={{ fontSize: 20, lineHeight: 1 }}>🧪</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    Công thức sản xuất
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {recipeProduct.item_name}
                  </div>
                </div>
                {recipe?.warnings && recipe.warnings.length > 0 && (
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 20, height: 20, padding: "0 6px", borderRadius: 10, background: "var(--accent-red)", color: "#fff", fontSize: 11, fontWeight: 700 }} title="Số cảnh báo công thức">
                    {recipe.warnings.length}
                  </span>
                )}
                <button className="btn btn-ghost" onClick={closeRecipe} style={{ padding: 4, minWidth: "auto" }} title="Đóng">
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>

              {/* COGS bar */}
              <div style={{ padding: "8px 20px", background: "var(--bg-input)", borderBottom: "1px solid rgba(255,255,255,0.10)", display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center", fontSize: 12 }}>
                <span style={{ color: "var(--text-secondary)" }}>COGS</span>
                <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>
                  {recipe ? formatVND(recipe.total_cogs) + "đ" : "..."}
                </span>
                {recipe && recipe.total_cogs > 0 && recipeProduct.default_price ? (
                  <>
                    <span style={{ color: "var(--text-tertiary)" }}>·</span>
                    <span style={{ fontWeight: 700, color: "var(--accent-green)" }}>
                      Margin {Math.round((1 - recipe.total_cogs / (recipeProduct.default_price || 1)) * 100)}%
                    </span>
                  </>
                ) : null}
              </div>

              {/* Body */}
              <div style={{ flex: 1, overflow: "auto", padding: "14px 16px 20px" }}>
                {recipeLoading ? (
                  <div style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 28, animation: "spin 1s linear infinite" }}>progress_activity</span>
                  </div>
                ) : (
                  <>
                    {recipeError && (
                      <div style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.40)", borderRadius: 8, padding: "10px 12px", marginBottom: 12, color: "var(--accent-red)", fontSize: 13, display: "flex", alignItems: "center", gap: 12 }}>
                        <span>⚠️ {recipeError}</span>
                        <button onClick={() => recipeProduct && openRecipe(recipeProduct)} style={{ marginLeft: "auto", color: "var(--accent-red)", textDecoration: "underline", background: "transparent", border: "none", cursor: "pointer", fontSize: 13 }}>
                          Thử lại
                        </button>
                      </div>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-tertiary)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.10em" }}>
                      Nguyên liệu · {recipeMaterials.length}
                    </div>
                    {recipeMaterials.length === 0 ? (
                      <div style={{ color: "var(--text-tertiary)", fontSize: 13, padding: "20px 0", textAlign: "center", background: "var(--bg-card)", borderRadius: 10, border: "1px dashed rgba(255,255,255,0.12)" }}>
                        Chưa có nguyên liệu.<br />Nhấn "Thêm nguyên liệu" để bắt đầu.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {recipeMaterials.map((m, idx) => (
                          <div key={m.material_item_id} style={{ background: "var(--bg-card)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: "10px 12px", transition: "background 150ms ease" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#15151a", e.currentTarget.style.background = "var(--bg-card-hover, #15151a)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "#0f0f12", e.currentTarget.style.background = "var(--bg-card, #0f0f12)")}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", wordBreak: "break-word" }}>{m.material_name}</div>
                                {m.base_unit && (
                                  <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>
                                    ĐVCB: {m.base_unit}
                                    {m.qty_base != null && m.qty_base !== m.qty_per_unit ? ` · quy đổi: ${m.qty_base}` : ""}
                                  </div>
                                )}
                              </div>
                              <button className="btn btn-ghost" onClick={() => removeMaterial(idx)} style={{ padding: 2, minWidth: "auto", color: "var(--accent-red)" }} title="Xoá nguyên liệu">
                                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
                              </button>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                              <input
                                type="number" inputMode="decimal" value={m.qty_per_unit}
                                onChange={(e) => updateMaterialQty(idx, Math.max(0, Number(e.target.value)))}
                                style={{ width: 60, textAlign: "right", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 6, padding: "4px 6px", fontSize: 13, background: "var(--bg-input)", color: "var(--text-primary)" }}
                              />
                              <select
                                value={m.unit || m.base_unit || "pcs"}
                                onChange={(e) => updateMaterialUnit(idx, e.target.value)}
                                title="Đơn vị tính"
                                style={{ border: "1px solid rgba(255,255,255,0.10)", borderRadius: 6, padding: "4px 6px", fontSize: 13, background: "var(--bg-input)", color: "var(--text-primary)", cursor: "pointer", maxWidth: 64 }}
                              >
                                {unitsForBase(m.base_unit).map((u) => (<option key={u} value={u}>{u}</option>))}
                              </select>
                              <span style={{ flex: 1, textAlign: "right", fontSize: 11, color: "var(--text-tertiary)" }}>{formatVND(m.unit_cost)}đ/u</span>
                              <span style={{ fontWeight: 700, fontSize: 13, color: "var(--accent-blue)", minWidth: 80, textAlign: "right" }}>{formatVND(m.subtotal)}đ</span>
                            </div>
                            <input
                              type="text"
                              placeholder="Ghi chú..."
                              value={m.note}
                              onChange={(e) => updateMaterialNote(idx, e.target.value)}
                              style={{ border: "none", background: "transparent", fontSize: 11, color: "var(--text-secondary)", width: "100%", outline: "none", padding: 0, marginTop: 6 }}
                            />
                          </div>
                        ))}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", marginTop: 4, background: "var(--bg-elevated)", borderRadius: 10, border: "1px solid rgba(255,255,255,0.10)" }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Tổng COGS</span>
                          <span style={{ fontWeight: 800, fontSize: 15, color: "var(--accent-blue)" }}>
                            {formatVND(recipeMaterials.reduce((s, m) => s + m.subtotal, 0))}đ
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Add material search */}
                    <div style={{ position: "relative", marginTop: 14 }}>
                      <button className="btn btn-ghost" onClick={() => setRecipeSearchOpen(!recipeSearchOpen)} style={{ fontSize: 12, fontWeight: 600, color: "var(--accent-blue)", padding: "8px 12px", borderRadius: 8, border: "1px dashed rgba(96,165,250,0.40)", background: "rgba(96,165,250,0.06)", width: "100%", justifyContent: "flex-start" }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "middle", marginRight: 4 }}>add</span> Thêm nguyên liệu
                      </button>
                      {recipeSearchOpen && (
                        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 10, background: "var(--bg-elevated)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.6)", overflow: "hidden" }}>
                          <input
                            type="text" placeholder="Tìm nguyên liệu..." value={recipeSearchQ}
                            onChange={(e) => handleRecipeSearch(e.target.value)}
                            autoFocus
                            autoComplete="off"
                            style={{ width: "100%", padding: "10px 14px", border: "none", borderBottom: "1px solid rgba(255,255,255,0.10)", background: "var(--bg-input)", fontSize: 13, outline: "none", color: "var(--text-primary)" }}
                          />
                          {recipeSearchResults.length > 0 && (
                            <div style={{ maxHeight: 220, overflow: "auto" }}>
                              {recipeSearchResults.map((m) => (
                                <div
                                  key={m.id}
                                  onClick={() => addMaterialToRecipe(m)}
                                  style={{ padding: "9px 14px", cursor: "pointer", fontSize: 12, borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", background: "transparent" }}
                                  onMouseEnter={(e) => (e.currentTarget.style.background = "#15151a", e.currentTarget.style.background = "var(--bg-card-hover, #15151a)")}
                                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                >
                                  <span style={{ color: "var(--text-primary)" }}>{m.item_name}</span>
                                  <span style={{ color: "var(--text-secondary)" }}>{m.cost ? formatVND(m.cost) + "đ" : "?"}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {recipeSearchQ.trim().length >= 1 && recipeSearchResults.length === 0 && (
                            <div style={{ padding: "10px 14px", fontSize: 12, color: "var(--text-secondary)" }}>Không tìm thấy</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Warnings */}
                    {recipe?.warnings && recipe.warnings.length > 0 && (
                      <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 10, background: "rgba(251,191,36,0.10)", border: "1px solid rgba(251,191,36,0.30)" }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "var(--status-amber)", marginBottom: 6, display: "flex", alignItems: "center", gap: 4 }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>warning</span> Cảnh báo
                        </div>
                        {recipe.warnings.map((w, i) => (
                          <div key={i} style={{ fontSize: 12, color: "var(--status-amber)", marginTop: 3, lineHeight: 1.4 }}>{w}</div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: "10px 16px", borderTop: "1px solid rgba(255,255,255,0.10)", display: "flex", gap: 8, justifyContent: "flex-end", background: "var(--bg-card)" }}>
                <button className="btn btn-ghost" onClick={closeRecipe}>Đóng</button>
                <button className="btn btn-primary" onClick={saveRecipe} disabled={recipeSaving} style={{ minWidth: 110 }}>
                  {recipeSaving ? <span className="material-symbols-outlined" style={{ fontSize: 14, animation: "spin 1s linear infinite" }}>progress_activity</span> : <><span className="material-symbols-outlined" style={{ fontSize: 14 }}>save</span> Lưu công thức</>}
                </button>
              </div>
            </div>
          </div>
          <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
        </>,
        document.body
      )}
    </GlassPanel>
  );
}
